import { ScreeningSummaryActions } from '../summary/ScreeningSummaryActions';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  Gauge,
  History,
  Loader2,
  RefreshCcw,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

import type { ReportTypeId } from '../../../config/reportTypes';
import { DECISION_STAGE_LABEL, getOfferableReportTypes, REPORT_TYPE_REGISTRY, reportTypeStage, type DecisionStage } from '../../../config/reportTypes';
import type { DerivedSiteVerdict } from '../../../lib/portfolio/deriveSiteVerdict';
import {
  readConclusionRiskItems,
  readReportStrengths,
} from '../../../lib/reportFindings';
import { BOILERPLATE_CAPTION, parseConditionScore, parseLaneFact } from '../../../lib/findings/laneItemStructure';
import {
  clusterFindings,
  detectScreeningContradictions,
  polarityFromImpact,
  resolvePolarity,
  type CrossBandContradiction,
  type FindingCandidate,
  type FindingCluster,
  type FindingPolarity,
} from '../../../lib/findings/findingClusters';
import { explainRefreshError } from '../../../lib/decisionAnalysis/explainRefreshError';
import { GenerationCountdown, useProgressStall, HEARTBEAT_STALL_MS } from '../GenerationProgress';
import { useGenerationHeartbeat } from '../../../store/generationHeartbeat';
import { isSentimentEvidenceWaitStage } from '../../../lib/sentiment/sentimentEvidenceReadiness';
import {
  SentimentEvidenceHoldNotice,
  SentimentEvidenceWaitingNotice,
  useSentimentEvidenceHold,
} from '../SentimentEvidenceNotice';
import { pathToGoPositiveDecisionAllowed } from '../../../lib/pathToGo/decisionSnapshot';
import type { PathToGoState } from '../../../lib/pathToGo/pathToGoOrchestrator';
import {
  latestUsableSavedReportOfType,
  planItemCompletesStage,
  reportCanContributeSavedResult,
} from '../../../lib/pathToGo/reportPlan';
import type {
  ReportLifecycle,
  ReportPlanItem,
} from '../../../lib/pathToGo/reportPlan';
import { flaggedFloorClause, type OpenAuditKind } from '../../../lib/audit/openAuditFlag';
import { displayAddress } from '../../../lib/siteAddressDisplay';
import {
  buildCallReadyFindingsModel,
  type CallReadyAnswer,
} from '../../../lib/findings/callReadyFindings';
import { cn } from '../../../lib/utils';
import { evaluatePersistedTestFitWorkflow } from '../../../lib/generationImpact/testFitWorkflow';
import type { SiteDecisionStageStatus } from '../../../lib/stage2/siteDecisionStage';
import type { BriefRouteHandler } from './brief/InstrumentParts';
import type { SiteEntity } from '../../../types/site';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { PathToGoDecisionRecord } from '../../../types/pathToGoDecision';
import type {
  SiteRagReport,
} from '../../../types/siteRag';
import {
  bandStatusPillVariant,
  bandToneClass,
} from '../../shared/verdictVisuals';
import { Button } from '../../ui/button';
import { StatusPill, type StatusPillVariant } from '../../ui/status-pill';
import { lifecyclePresentation, truthQuestionCountOf as sharedTruthQuestionCountOf } from '../../../lib/decisionAnalysis/lifecyclePresentation';
import { AnalysisLifecycleStatusPill } from '../AnalysisLifecycleStatusPill';
import { buildStageBoards, countStartableWork, screeningOutcomeLabel, screeningScoreShort, withScreeningOutcome, startableWorkLabel, type StageBoardModel, type StageBoardState } from '../../../lib/pathToGo/stageBoards';
import type { DecisionStagesRollup, DecisionStageRollup } from '../../../lib/executiveDecisionRollup';
import { StageStrip, type StageStripDecision } from '../twoStage/StageStrip';
import { ScenarioStep, StageBoard, StageGlyph, StageRow, StageRowMeta, StageRowName } from '../twoStage/StageBoard';
import { laterLabel } from '../../../lib/pathToGo/reportPlanLanes';
import { analysisForReport, DecisionAnalysisExpansion } from './DecisionAnalysisSections';
import { presentReportsText } from '../../../lib/reportRendering';
import { isWithheldNarrativeNotice } from '../../../lib/reportRendering/scrubInternalSentinels';
import { WithheldNarrativeNotice } from '../report/WithheldNarrativeNotice';
import { CallReadyFindingsSummary, type PhaseDestinationLabels } from './CallReadyFindingsSummary';
import type { EconomicContributionSiteInputs } from '../../../types/economicContribution';
import type { EconomicEvidenceResearchController } from './useEconomicEvidenceResearch';
import type { EconomicWorkforceProfileController } from './useEconomicWorkforceProfile';
import { VERDICT_WITHHELD_LABEL } from '../../../lib/portfolio/decisionBands';
import type { NextAction } from '../../../lib/pathToGo/nextAction';
import type { SiteComposite } from '../../../types/portfolioComposite';
import { buildSiteNextStep } from '../../../lib/pathToGo/siteNextStep';
import { DecisionPath } from '../../ui/instrument/DecisionPath';
import { SiteRecommendedPath } from '../overview/SiteRecommendedPath';
import { selectDecisionStageReports } from '../../../lib/findings/decisionStageReports';
import { BriefScreeningDecision, type BriefScreeningReviewData } from './BriefScreeningDecision';

/** Dispatch a report-plan action; a row's action passes the row's analysis type. */
type PlanActionHandler = (
  action: NonNullable<AnalysisMatrixRow['primaryAction']>,
  analysisType?: ReportTypeId,
) => void;

export interface FindingsDossierProps {
  site: SiteEntity;
  /**
   * Site-level destination. The released navigation promotes Decision and
   * Stakeholder Brief beside Analyses; `combined` remains only for legacy
   * callers and feature-flag rollback.
   */
  surface?: 'combined' | 'analyses' | 'decision' | 'stakeholder';
  /**
   * Compatibility analysis records. Today these are the persisted report
   * payloads; the component deliberately calls them analyses because the live
   * web findings, rather than their paginated presentation, are the product.
   */
  analyses: readonly SiteRagReport[];
  /** Whether the report and semantic-analysis queries have reached a terminal state. */
  analysesState?: 'loading' | 'settled';
  /**
   * Additive call-ready composition inside Findings. This changes only the
   * dossier's internal lead section; the site still opens on Atrium/Home.
   */
  callReadyEnabled?: boolean;
  /** Independently flagged economic cards inside the call-ready answer sheet. */
  economicContributionEnabled?: boolean;
  onRunEconomicScreen?: (inputs: EconomicContributionSiteInputs) => Promise<void>;
  /** Independent public-record job state; never participates in report hydration. */
  economicResearch?: EconomicEvidenceResearchController;
  /** Location-derived federal workforce state; independent of MW and report hydration. */
  economicWorkforceProfile?: EconomicWorkforceProfileController;
  derivedVerdict?: DerivedSiteVerdict | null;
  pathToGo?: PathToGoState | null;
  /** Host-computed action shared with Home; never resolved again by this surface. */
  journey?: { state: PathToGoState; nextAction: NextAction; loading?: boolean } | null;
  composite?: SiteComposite | null;
  onRunNextAction?: (action: NextAction) => void;
  /**
   * Live SED, required to evaluate Test Fit workspace readiness accurately —
   * `evaluatePersistedTestFitWorkflow` reports `sed-rebuild-required` when it
   * is absent, so omitting it would understate a healthy site.
   */
  sed?: SiteEvidenceDocument | null;
  /** Runtime availability of the Generation & Impact workflow. */
  generationImpactEnabled?: boolean;
  /**
   * Optional standalone requirement list for callers that do not yet have a
   * Path-to-GO projection. Path-to-GO's reportPlan remains authoritative when
   * both are present.
   */
  requiredAnalysisTypes?: readonly ReportTypeId[];
  /**
   * Exact report or semantic-analysis identity requested by Path to GO. The
   * dossier selects the owning section, expands the matching matrix row, and
   * scrolls it into view once mounted.
   */
  focusAnalysisId?: string | null;
  onFocusAnalysisHandled?: () => void;
  onOpenAnalysis?: (analysisId: string) => void;
  /**
   * Open the promoted Analyses destination. Decision uses this for live-run
   * progress because its own split surface intentionally does not mount the
   * analysis matrix.
   */
  onOpenAnalyses?: (analysisId: string | null) => void;
  /**
   * Open the work of a phase (Stakeholder Brief next steps; the Decision
   * surface's "Review and record decision" passes `decide`). The host routes
   * each phase to the tab that owns it — Path to GO removal, stage 2.
   */
  briefDecisionStage?: SiteDecisionStageStatus;
  /** Review-only selection; never changes the canonical workflow stage or gates. */
  decisionReviewStage?: 1 | 2;
  onDecisionReviewStageChange?: (stage: 1 | 2) => void;
  onOpenBriefRoute?: BriefRouteHandler;
  onOpenPhase?: (
    phase: 'build' | 'verify' | 'decide',
    context?: { questionId: CallReadyAnswer['id']; questionLabel: string },
  ) => void;
  /**
   * Start an analysis TYPE that has no record yet.
   *
   * (Reruns of tracked types are NOT offered here — William, 2026-08-11:
   * regeneration happens only from the report page.)
   * Optional analyses (Custom Brief and friends) are absent from the
   * required matrix BY DESIGN — they are `COVERAGE_META_REPORT_TYPES`, so no
   * readiness surface evaluates them — which left them with no entry point on
   * this tab at all. Omit to render the optional list without actions.
   */
  onLaunchAnalysis?: (analysisType: ReportTypeId) => void;
  /** True for Datacrate admins; widens the offerable set to beta types. */
  isDatacrateAdmin?: boolean;
  /**
   * Route to whatever is blocking an analysis that has no record yet (missing
   * intent fields, an unconfigured Generation & Impact scenario, absent
   * evidence). Omit to render the row's status as text instead of an action —
   * the Action column must never show a label that does nothing.
   */
  onResolveInputs?: (analysisType: ReportTypeId) => void;
  /**
   * Dispatch a report-plan `PathAction` — the SAME handler Path to GO's Analyse
   * stage used. Supplying this makes an unstarted row's action identical to Path
   * to GO's (same label, same destination) instead of a generic fallback. A
   * row's action also passes the row's analysis type, so an action that does
   * not carry one (`engineering-source`) can still open that analysis's inputs.
   */
  onPlanAction?: PlanActionHandler;
  /**
   * The tab each Stakeholder Brief phase route opens, by name ("Continue in
   * <tab>"). The host resolves it from the same router `onOpenPhase` runs, so
   * the label names where the click lands. A missing phase reads "Continue".
   */
  phaseDestinationLabels?: PhaseDestinationLabels;
  /**
   * Refresh-failure reasons by analysis type (from the decision-analyses
   * operational state). A row whose lifecycle is `failed` renders its
   * reason under the state pill so a crashed Retry is VISIBLE — the live
   * gap was a 3s job crash silently reverting to "Refresh required".
   */
  refreshErrorByType?: Partial<Record<ReportTypeId, string>>;
  autoRebindByType?: Partial<Record<ReportTypeId, { exhausted: boolean; streak?: number; optionalEvidenceSections?: string[] }>>;
  /** Live refresh progress (job progress + stage) for queued/generating rows.
   *  `heartbeatAt`/`runId` come from the joined generation-job run: the
   *  heartbeat powers the stall indicator, the id powers Cancel. */
  refreshProgressByType?: Partial<Record<ReportTypeId, { progress?: number; stage?: string; heartbeatAt?: string | null; runId?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>>;
  /** Cancel an in-flight generation run (stall indicator affordance). */
  onCancelRun?: (runId: string) => void;
  /**
   * In-flight GENERATION runs by report type, from the generation-job store.
   * Distinct from `refreshProgressByType`, which is keyed by decision-analysis
   * DOMAIN and so can never carry `custom-brief` / `executive-summary` — the
   * two types the Optional analyses tab exists to launch.
   */
  inflightByType?: Partial<Record<ReportTypeId, { progress?: number; progressStage?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>>;
  decisionRecords?: readonly PathToGoDecisionRecord[];
  /** Independently read Stage 1 history, distinct from live recommendation evidence. */
  briefScreening?: BriefScreeningReviewData;
  onReviewScreeningDecision?: () => void;
  className?: string;
}

/**
 * Shape shared with `findingClusters.FindingSourceRef` — a lane row's sources
 * come straight off its cluster, so the two must stay structurally identical.
 */
interface AnalysisSource {
  id: string;
  type: ReportTypeId;
  label: string;
  versionLabel: string;
}

/**
 * A lane row is now a CLUSTER of statements rather than a single string.
 * `findingClusters` merges the same claim stated by different analyses, keeps
 * every contributing statement verbatim, and stamps the row when the analyses
 * disagree — see that module for why the merge is a detector, not a tidy-up.
 */
type DossierEvidenceItem = FindingCluster;

/**
 * THE CHIP'S TOOLTIP, per audit kind (round 9).
 *
 * Three cases, because there are three things we can honestly say:
 *   - `'flagged'` — the flag stands and nothing itemisable does, so the
 *     tooltip says so rather than sending the reader to an empty list;
 *   - an itemised kind — findings really are open on the report page;
 *   - kind ABSENT — a plan item built without it (older shape, hand-built
 *     fixture). We do not know which of the two above holds, so the wording
 *     asserts neither: it names the flag and the destination only.
 */
export function auditChipTooltip(kind: OpenAuditKind | undefined): string {
  if (kind === 'flagged') {
    const clause = flaggedFloorClause();
    return `The quality audit flagged this analysis. ${clause.charAt(0).toUpperCase()}${clause.slice(1)}, `
      + 'so there is nothing itemised to review.';
  }
  if (kind) return 'The quality audit flagged findings on this analysis. Open it to review them.';
  return 'The quality audit flagged this analysis. Open it to review the audit.';
}

interface AnalysisMatrixRow {
  type: ReportTypeId;
  label: string;
  role: 'required' | 'intent-relevant' | 'available';
  lifecycle: ReportLifecycle | 'available' | 'missing';
  satisfiesDecisionGate: boolean | null;
  /** Whether the successful result is current, saved-but-stale, or unavailable. */
  calculationStatus?: ReportPlanItem['calculationStatus'];
  /** Current screening can complete this stage while its decision remains withheld. */
  completesStage: boolean | null;
  /**
   * The analysis cannot be produced yet because its OWN inputs are absent —
   * Financial without a model, Supply Chain without an equipment basis, Test Fit
   * without a promoted generation scenario. Carried from the plan item's
   * eligibility group so this matrix and Path to GO > Analyse agree about what
   * is actually startable. Without it, Findings numbered a blocked analysis as
   * priority #1 while Path to GO had already moved it to "Available later".
   */
  awaitingPrerequisites?: boolean;
  /** The plan's own one-line reason for the `available-later` lane (two-stage lock,
   *  missing scenario, model or BOM); shown beside the lane label so a required
   *  row never reads "Ready to analyse" while it cannot be started. */
  awaitingReason?: string;
  /** The plan's reason code behind `awaitingReason` (e.g. a STALE promoted scenario). */
  awaitingReasonCode?: string;
  /** The plan's full sentence behind the short `awaitingReason` — tooltip and assistive text. */
  awaitingDetail?: string;
  /** Two-stage pipeline stage of the plan item (registry); groups the
   *  available-later rows under the same heading Path to GO > Analyse uses. */
  stage?: DecisionStage;
  /** AUDIT-ADVISORY (2026-08-22): the audit flagged this analysis; never a gate. */
  auditRecommended?: boolean;
  /**
   * What the flag is made of — carried from the plan item (round 9). The
   * chip's tooltip and the hero rollup below both put the flag in a SENTENCE
   * about findings, and on a FLAGGED-FLOOR analysis (`'flagged'`: every stamp
   * superseded, nothing itemised, the disclosure's count is the floor of 1)
   * there are no findings to open or to fold into the verdict.
   */
  auditKind?: OpenAuditKind;
  blockingReason?: string;
  analysis?: SiteRagReport;
  /**
   * A saved record exists for the row: the plan names a report or semantic
   * analysis (Path to GO's `hasRecord`), or this matrix resolved one. Keeps a
   * saved row out of the "to run" count (`startableWorkKind`) and feeds the
   * board header's "· K saved". Display only — never a gate input.
   */
  hasRecord: boolean;
  /**
   * Live workspace evidence for a row whose readiness is owned by a workflow
   * rather than a generated analysis document — today only Test Fit, whose
   * authority lives in the Generation & Impact workspace (promoted scenario +
   * layout head). Path to GO already reads this via
   * `evaluatePersistedTestFitWorkflow`; surfacing it here stops Findings
   * reporting "No analysis record" for a site whose G&I work plainly exists.
   *
   * DISPLAY ONLY — `satisfiesDecisionGate` still comes from the report plan, so
   * the fail-closed decision gate is unchanged.
   */
  workflowEvidence?: { title: string; detail: string; ready: boolean };
  /**
   * The SAME `primaryAction` Path to GO's Analyse stage renders for this row,
   * carried straight off the report plan. Both surfaces already derive from
   * `pathToGo.reportPlan`, so their STATES agreed — but their ACTIONS did not:
   * Path to GO routed to the precise blocker ("Configure Generation & Impact",
   * "Complete the financial model") while this matrix offered one generic
   * "Resolve inputs". Reusing the plan's action makes the label and destination
   * identical on both surfaces by construction, instead of two hand-maintained
   * lists that drift.
   */
  primaryAction?: ReportPlanItem['primaryAction'];
  /**
   * Stale only because optional currency evidence (public sentiment, economic
   * evidence) is newer than the analysis — carried from the plan item. The row
   * reads "Newer evidence available": nothing re-runs it automatically.
   */
  optionalEvidenceOnly?: boolean;
}

interface RevisionHistoryRow {
  key: string;
  analysis: string;
  revision: string;
  createdAt: string;
  /**
   * Persisted readiness state, or null for pre-readiness revisions. Null renders
   * NO pill: a "legacy" badge described our storage history, not the revision.
   */
  state: string | null;
  /** Why this revision is in its state, in the reader's language. */
  cause: string;
  /** What measurably moved against the revision before it. Empty for the first. */
  changes: string[];
}

interface ExportHistoryRow {
  key: string;
  analysis: string;
  format: string;
  state: 'staged' | 'ready' | 'failed';
  createdAt: string;
  detail: string;
}

export interface FindingsDossierModel {
  latestAnalyses: SiteRagReport[];
  matrixMode: 'required' | 'available';
  matrixRows: AnalysisMatrixRow[];
  findings: DossierEvidenceItem[];
  risks: DossierEvidenceItem[];
  gaps: DossierEvidenceItem[];
  conditions: DossierEvidenceItem[];
  /**
   * Subjects where the audit screening pass and the analyses read the same
   * fact in opposite directions. Surfaced on the screening band so the page
   * states the disagreement instead of quietly printing both readings in
   * different lanes.
   */
  screeningContradictions: CrossBandContradiction[];
  revisionHistory: RevisionHistoryRow[];
  exportHistory: ExportHistoryRow[];
  decisionSummary?: string;
}

/**
 * Stall indicator + Cancel for a queued/generating matrix row. An hour-long
 * job with a frozen phase label is indistinguishable from a hang (live
 * 2026-08-11: two Redhead runs sat 25-50 min on one phase with no signal and
 * no way out from this surface). Keyed off the server heartbeat when the run
 * carries one — the runner refreshes it every ≤30s while genuinely alive — so
 * the note appears only when beats STOP, never during a long honest phase.
 */
export const AnalysisRunHealth: React.FC<{
  rowType: ReportTypeId;
  progress?: { stage?: string; progress?: number; heartbeatAt?: string | null; runId?: string };
  onCancelRun?: (runId: string) => void;
}> = ({ rowType, progress, onCancelRun }) => {
  // The beat is read from the store by run id — it deliberately does NOT ride
  // on the progress/run objects (see `generationHeartbeats`), so this row is
  // the only thing that re-renders on a heartbeat tick. An explicit
  // `progress.heartbeatAt` still wins for callers that already hold one.
  const liveHeartbeatAt = useGenerationHeartbeat(progress?.runId);
  const heartbeatAt = progress?.heartbeatAt ?? liveHeartbeatAt;
  const hasHeartbeat = Boolean(heartbeatAt);
  const stallKey = heartbeatAt ?? `${progress?.stage ?? ''}:${progress?.progress ?? ''}`;
  const stalled = useProgressStall(stallKey, true, hasHeartbeat ? HEARTBEAT_STALL_MS : undefined);
  if (!stalled) return null;
  return (
    <div
      className="mt-1 flex items-center gap-2"
      data-testid={`findings-run-stalled-${rowType}`}
    >
      <p className="text-[11px] leading-snug text-warning">
        {hasHeartbeat
          ? 'No heartbeat from the job for 2+ minutes — it may be stalled.'
          : 'No progress for several minutes — the job may be stalled.'}
      </p>
      {progress?.runId && onCancelRun ? (
        <button
          type="button"
          className="text-[11px] font-medium text-text-secondary underline-offset-2 hover:text-danger hover:underline"
          onClick={() => onCancelRun(progress.runId!)}
          data-testid={`findings-run-cancel-${rowType}`}
        >
          Cancel run
        </button>
      ) : null}
    </div>
  );
};

/**
 * The explained refresh error under a failed (or auto-refresh-exhausted) row:
 * a one-line headline and, when there is one, the detail. Shared by the stage
 * rows and the decision-material additional analysis (Public Sentiment).
 */
const RefreshErrorNote: React.FC<{
  error: string | undefined;
  truthQuestionCount: number;
  autoRebind?: { exhausted: boolean; streak?: number; optionalEvidenceSections?: string[] };
  informational?: boolean;
  'data-testid': string;
}> = ({ error, truthQuestionCount, autoRebind, informational, 'data-testid': testId }) => {
  if (!error) return null;
  const explained = explainRefreshError(error, truthQuestionCount, {
    autoRebindExhausted: autoRebind?.exhausted,
    autoRebindStreak: autoRebind?.streak,
    optionalEvidenceSections: autoRebind?.optionalEvidenceSections,
  });
  if (!explained) return null;
  return (
    <div
      className="max-w-[16rem] space-y-0.5 whitespace-normal"
      title={explained.raw}
      data-testid={testId}
    >
      <p className={cn('break-words line-clamp-3 text-[11px] leading-snug', informational ? 'text-text-secondary' : 'text-danger')}>
        {explained.headline}
      </p>
      {explained.detail ? (
        <p className="break-words line-clamp-3 text-[10px] leading-snug text-text-muted">
          {explained.detail}
        </p>
      ) : null}
    </div>
  );
};

const LIFECYCLE_LABEL: Record<AnalysisMatrixRow['lifecycle'], string> = {
  'needs-input': 'Needs evidence',
  'dependency-blocked': 'Setup needed',
  ready: 'Ready to analyse',
  queued: 'Queued',
  generating: 'Analysing',
  preliminary: 'Preliminary',
  preview: 'Preview only',
  current: 'Current',
  'refresh-needed': 'Refresh required',
  'quality-held': 'Quality review',
  failed: 'Failed',
  'failed-coverage': 'Coverage blocked',
  unavailable: 'Unavailable',
  available: 'Available',
  missing: 'Missing',
};

/**
 * Answerability + held-softening are the SHARED `lifecyclePresentation`
 * module's job now — Path to GO's GenerateStage rendered from the same source,
 * which is what keeps the two views' tags from disagreeing about the same
 * analysis again. Re-exported so existing imports (tests) keep resolving.
 */
export { truthQuestionCountOf } from '../../../lib/decisionAnalysis/lifecyclePresentation';

function analysisType(report: SiteRagReport): ReportTypeId {
  return report.reportType ?? 'feasibility';
}

function analysisLabel(type: ReportTypeId): string {
  return REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type;
}

function analysisTimestamp(report: SiteRagReport): number {
  const value = Date.parse(report.updatedAt ?? report.createdAt ?? '');
  return Number.isFinite(value) ? value : 0;
}

function versionLabel(report: SiteRagReport): string {
  const major = report.version ?? 1;
  const minor = report.minorVersion ?? 0;
  return minor > 0 ? `v${major}.${minor}` : `v${major}`;
}

function sourceFor(report: SiteRagReport): AnalysisSource {
  const type = analysisType(report);
  return {
    id: report.id,
    type,
    label: analysisLabel(type),
    versionLabel: versionLabel(report),
  };
}

/**
 * Collect one candidate statement for a lane.
 *
 * `titleKind` and `fieldPolarity` are the two things only the CALLSITE knows,
 * and both change the output materially:
 *
 *   - `titleKind` says whether `title` is a heading the analysis authored
 *     (`contextualKeyFindings[].title`) or a prose sentence lifted out of a
 *     conclusion array. It decides which member wins a merged row's headline;
 *     ranking on length alone always chose the sentence.
 *   - `fieldPolarity` is the polarity the FIELD defines. `conclusion
 *     .topStrengths[]` is favourable by definition and `topConstraints[]`
 *     unfavourable by definition, even though neither carries an impact value.
 *     It is NOT the lane's polarity — the Key findings lane genuinely holds a
 *     mix today, so reading polarity off the lane would assert the opposite of
 *     the truth for every negative finding sitting in it.
 */
const SCORECARD_METRIC_PROOF_COPY: Readonly<Record<string, { label: string; proof: string }>> = {
  land_avail: {
    label: 'Land Availability',
    proof: 'Confirm buildable parcel acreage, property boundaries, and usable site area from survey and Test Fit evidence.',
  },
  topography: {
    label: 'Topography / Slope',
    proof: 'Verify parcel grading, average slope, and earthwork constraints from a topographic or ALTA survey.',
  },
  flood_risk: {
    label: 'Flood Risk',
    proof: 'Confirm FEMA flood-zone designation (100-year / 500-year floodplain) and finished-floor elevation requirements.',
  },
  seismic_risk: {
    label: 'Seismic Risk',
    proof: 'Verify USGS seismic design category, peak ground acceleration, and geotechnical fault proximity.',
  },
  air_quality: {
    label: 'Air Quality (AQI)',
    proof: 'Confirm county EPA Green Book NAAQS attainment status and air-permitting thresholds.',
  },
  dist_sub: {
    label: 'Distance to Substation',
    proof: 'Verify the point-of-interconnection substation location, line routing distance, and right-of-way.',
  },
  grid_cap: {
    label: 'Grid Capacity',
    proof: 'Obtain a utility will-serve or capacity study confirming deliverable MW and target energization schedule.',
  },
  firm_capacity: {
    label: 'Firm Grid Capacity',
    proof: 'Obtain a utility will-serve or capacity study confirming firm deliverable MW and energization schedule.',
  },
  inter_comp: {
    label: 'Interconnection Complexity',
    proof: 'Confirm transmission queue position, network upgrade scope, and utility interconnection study timeline.',
  },
  electricity_rate: {
    label: 'Electricity Rate',
    proof: 'Verify applicable large-load utility tariff schedule and delivered power pricing.',
  },
  amb_temp: {
    label: 'Avg Ambient Temp',
    proof: 'Confirm ASHRAE design dry-bulb temperature profile for cooling equipment sizing.',
  },
  wet_bulb: {
    label: 'Wet Bulb Temp',
    proof: 'Confirm ASHRAE design wet-bulb temperature profile for heat-rejection sizing.',
  },
  water_stress: {
    label: 'Water Stress Index',
    proof: 'Verify basin water stress, municipal water allocation, and drought-contingency restrictions.',
  },
  nuclear_friend: {
    label: 'Nuclear Permitting',
    proof: 'Confirm state statutory framework and regulatory stance for advanced nuclear siting.',
  },
  zoning: {
    label: 'Zoning Alignment',
    proof: 'Confirm current parcel zoning classification, permitted industrial/data-center use, and entitlement conditions with the local jurisdiction.',
  },
  env_const: {
    label: 'Environmental Constraints',
    proof: 'Commission a Phase I ESA and wetlands/habitat delineation to confirm site environmental constraints.',
  },
  reactor_proximity: {
    label: 'NRC Reactor Proximity',
    proof: 'Verify distance to operating nuclear facilities and any NRC siting or emergency-planning zone overlap.',
  },
  road_prox: {
    label: 'Road Proximity',
    proof: 'Confirm heavy-haul road access, bridge weight limits, and arterial highway connectivity.',
  },
  port_prox: {
    label: 'Network Latency (Screening)',
    proof: 'Run the optional Connectivity Feasibility Study or upload carrier latency and route quotes.',
  },
  ix_proximity: {
    label: 'IX / Data Center Proximity',
    proof: 'Verify long-haul fiber routes, carrier diversity, and proximity to internet exchange points.',
  },
  btm_tech_support: {
    label: 'BTM Tech Support',
    proof: 'Verify local community and regulatory sentiment toward on-site behind-the-meter generation.',
  },
  datacenter_attitude: {
    label: 'Data-Center Attitude',
    proof: 'Assess local municipal and community receptivity to large-scale data-center development.',
  },
  media_tone: {
    label: 'Media Tone',
    proof: 'Review recent local media coverage for infrastructure, water, or power-grid opposition.',
  },
  docket_activity: {
    label: 'Docket Activity',
    proof: 'Check utility commission dockets for contested large-load tariffs or intervention proceedings.',
  },
  economics: {
    label: 'Financial Viability',
    proof: 'Validate capital cost, LCOE, and project return assumptions with site-specific quotes.',
  },
  typed_risk: {
    label: 'Typed Risk Matrix',
    proof: 'Complete domain risk verification across permitting, power, water, and schedule.',
  },
};

const GENERIC_METRIC_GAP_DETAIL_RE =
  /^(?:the source report marked this metric as missing evidence\.?|(?:[^:]+:\s*)?(?:unassessed|evidence is not specified|not specified(?:\s*\([^)]*\))?)\.?)$/i;

function resolveGapPresentation(
  report: SiteRagReport,
  rawTitle: unknown,
  rawDetail?: unknown,
): { title: string; detail?: string } {
  const title = typeof rawTitle === 'string' ? rawTitle.trim() : '';
  const detail = typeof rawDetail === 'string' && rawDetail.trim() ? rawDetail.trim() : undefined;
  if (!title) return { title: '' };

  const metricMatch = /^metric:([a-z0-9_-]+):([a-z0-9_-]+)$/i.exec(title);
  const categoryId = metricMatch?.[1]?.toLowerCase();
  const metricId = metricMatch?.[2]?.toLowerCase();

  let matchedCategoryLabel: string | undefined;
  let matchedMetricLabel: string | undefined;
  let matchedMetricExplanation: string | undefined;
  let matchedMetricId = metricId;

  for (const cat of report.categories ?? []) {
    for (const m of cat.metrics ?? []) {
      const idMatch = metricId && m.id.toLowerCase() === metricId && (!categoryId || cat.id.toLowerCase() === categoryId);
      const labelMatch = !metricId && m.label.trim().toLowerCase() === title.toLowerCase();
      if (idMatch || labelMatch) {
        matchedCategoryLabel = cat.label;
        matchedMetricLabel = m.label;
        matchedMetricExplanation = m.explanation?.trim();
        matchedMetricId = m.id.toLowerCase();
        break;
      }
    }
    if (matchedMetricLabel) break;
  }

  const entry = matchedMetricId ? SCORECARD_METRIC_PROOF_COPY[matchedMetricId] : undefined;
  const resolvedTitle = matchedMetricLabel
    ?? entry?.label
    ?? (metricId
      ? metricId.replace(/[_-]+/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
      : title);

  const isGenericDetail = !detail || GENERIC_METRIC_GAP_DETAIL_RE.test(detail);
  const hasSpecificCategoryExplanation = matchedMetricExplanation
    && !GENERIC_METRIC_GAP_DETAIL_RE.test(matchedMetricExplanation);

  let resolvedDetail = detail;
  if (isGenericDetail) {
    if (hasSpecificCategoryExplanation && entry?.proof) {
      resolvedDetail = `${matchedCategoryLabel ? `${matchedCategoryLabel}: ` : ''}${matchedMetricExplanation} ${entry.proof}`;
    } else if (hasSpecificCategoryExplanation) {
      resolvedDetail = `${matchedCategoryLabel ? `${matchedCategoryLabel}: ` : ''}${matchedMetricExplanation}`;
    } else if (entry?.proof) {
      resolvedDetail = matchedCategoryLabel ? `${matchedCategoryLabel}: ${entry.proof}` : entry.proof;
    }
  }

  return {
    title: resolvedTitle,
    ...(resolvedDetail ? { detail: resolvedDetail } : {}),
  };
}

function addCandidate(
  target: FindingCandidate[],
  report: SiteRagReport,
  title: unknown,
  options: {
    detail?: unknown;
    severity?: unknown;
    titleKind?: 'headline' | 'statement';
    fieldPolarity?: FindingPolarity;
  } = {},
): void {
  if (typeof title !== 'string' || !title.trim()) return;
  const detail = typeof options.detail === 'string' && options.detail.trim()
    ? options.detail.trim()
    : undefined;
  const severity = typeof options.severity === 'string' && options.severity.trim()
    ? options.severity.trim()
    : undefined;
  target.push({
    title: title.trim(),
    ...(detail ? { detail } : {}),
    ...(severity ? { severity } : {}),
    titleKind: options.titleKind ?? 'statement',
    polarity: resolvePolarity(severity, options.fieldPolarity ?? 'neutral'),
    source: sourceFor(report),
  });
}

function latestAnalysesForSite(
  site: SiteEntity,
  analyses: readonly SiteRagReport[],
  pathToGo: PathToGoState | null | undefined,
): SiteRagReport[] {
  const sorted = analyses
    .filter(report => !report.siteId || report.siteId === site.id)
    .slice()
    .sort((left, right) => {
      const byTime = analysisTimestamp(right) - analysisTimestamp(left);
      return byTime !== 0 ? byTime : right.id.localeCompare(left.id);
    });
  const planByType = new Map(
    (pathToGo?.reportPlan ?? []).map(item => [item.type, item] as const),
  );
  const latest = new Map<ReportTypeId, SiteRagReport>();
  for (const report of sorted) {
    const type = analysisType(report);
    if (latest.has(type)) continue;
    const plan = planByType.get(type);
    const semantic = plan?.decisionEvidenceSource === 'semantic-analysis'
      ? sorted.find(candidate => (
          analysisType(candidate) === type
          && (
            candidate.decisionAnalysis?.analysisId === plan.analysisId
            || candidate.analysisRevision?.analysisId === plan.analysisId
          )
        ))
      : undefined;
    const exact = plan?.calculationReportId
      ? sorted.find(candidate => candidate.id === plan.calculationReportId)
      : undefined;
    latest.set(
      type,
      semantic
        ?? exact
        ?? latestUsableSavedReportOfType(sorted, type)
        ?? report,
    );
  }
  return [...latest.values()];
}

function uniqueTypes(types: readonly ReportTypeId[]): ReportTypeId[] {
  return [...new Set(types)];
}

/**
 * Rows whose readiness is owned by a WORKSPACE rather than a generated
 * analysis. Test Fit is the only one today: Path to GO makes it required via
 * `requiresGenerationImpactTestFit` and proves it from the Generation & Impact
 * workspace, so Findings must be able to read the same source or it reports
 * "No analysis record" for work the user can plainly see on the G&I tab.
 */
const WORKFLOW_BACKED_TYPES = new Set<ReportTypeId>(['test-fit']);

function buildMatrixRows(
  latestAnalyses: readonly SiteRagReport[],
  pathToGo: PathToGoState | null | undefined,
  requiredAnalysisTypes: readonly ReportTypeId[] | undefined,
  workflowContext: {
    site: SiteEntity;
    sed: SiteEvidenceDocument | null | undefined;
    generationImpactEnabled: boolean | undefined;
    showValidationRack?: boolean;
  },
  allAnalyses: readonly SiteRagReport[] = latestAnalyses,
): Pick<FindingsDossierModel, 'matrixMode' | 'matrixRows'> {
  // Evaluated at most once per model build, and only when a workflow-backed
  // row actually lacks an analysis document.
  let workflowEvidenceMemo: AnalysisMatrixRow['workflowEvidence'] | null | undefined;
  const resolveWorkflowEvidence = (
    type: ReportTypeId,
    hasAnalysis: boolean,
  ): AnalysisMatrixRow['workflowEvidence'] => {
    if (hasAnalysis || !WORKFLOW_BACKED_TYPES.has(type)) return undefined;
    if (workflowEvidenceMemo === undefined) {
      const readiness = evaluatePersistedTestFitWorkflow(
        workflowContext.site,
        workflowContext.sed,
        workflowContext.generationImpactEnabled === undefined
          ? {}
          : { generationImpactEnabled: workflowContext.generationImpactEnabled },
      );
      // A row that is not applicable to Generation & Impact at all (grid-only
      // intent) has no workspace evidence to show — leave it to the normal
      // "no analysis" copy rather than inventing a green state.
      workflowEvidenceMemo = readiness.applicability.status === 'grid-only'
        ? null
        : { title: readiness.title, detail: readiness.message, ready: readiness.ready };
    }
    return workflowEvidenceMemo ?? undefined;
  };
  const latestByType = new Map(
    latestAnalyses.map(report => [analysisType(report), report]),
  );
  // The working matrix includes everything the current intent makes useful
  // now. Only `required` rows participate in the readiness gate below;
  // intent-relevant rows stay visible without becoming artificial blockers.
  const runNowPlan = pathToGo?.reportPlan.filter(item => (
    item.eligibility?.group === 'run-now'
    || (!item.eligibility && item.role === 'required')
  )) ?? [];
  const requiredTypes = uniqueTypes(
    pathToGo
      ? [...pathToGo.requiredReports, ...runNowPlan.map(item => item.type), ...(workflowContext.showValidationRack ? pathToGo.reportPlan.filter(item => reportTypeStage(item.type) === 2 && item.type !== 'interconnection').map(item => item.type) : [])]
      : (requiredAnalysisTypes ?? []),
  );

  if (pathToGo || requiredAnalysisTypes) {
    // Keyed over the FULL plan, not just the run-now slice. A required analysis
    // routed to `available-later` — Test Fit without its generation-impact
    // inputs — is still in `requiredTypes` via `requiredReports`, but keying
    // only on run-now left it with NO plan entry, so it fell through to the
    // placeholder branch below and silently lost its eligibility, lifecycle,
    // blocking reason and primary action. It was then indistinguishable from an
    // un-started analysis and got numbered as startable work, which is why
    // Findings showed a blocked Test Fit as priority 1 while Path to GO had
    // already moved it to "Available later".
    const planByType = new Map<ReportTypeId, ReportPlanItem>(
      (pathToGo?.reportPlan ?? runNowPlan).map(item => [item.type, item]),
    );
    return {
      matrixMode: 'required',
      matrixRows: requiredTypes.map((type): AnalysisMatrixRow => {
        const plan = planByType.get(type);
        const preferredReportId = plan?.reportId ?? plan?.calculationReportId;
        const bySemanticId = plan?.analysisId
          ? allAnalyses.find(item => (
              analysisType(item) === type
              && (
                item.decisionAnalysis?.analysisId === plan.analysisId
                || item.analysisRevision?.analysisId === plan.analysisId
              )
            ))
          : undefined;
        const analysis = preferredReportId
          ? allAnalyses.find(item => item.id === preferredReportId)
            ?? latestAnalyses.find(item => item.id === preferredReportId)
            ?? bySemanticId
            ?? (plan?.calculationReportId
              ? latestAnalyses.find(item => item.id === plan.calculationReportId)
                ?? allAnalyses.find(item => item.id === plan.calculationReportId)
              : undefined)
            ?? latestByType.get(type)
          : bySemanticId ?? latestByType.get(type);
        const workflowEvidence = resolveWorkflowEvidence(type, Boolean(analysis));
        if (plan) {
          return {
            type,
            label: analysisLabel(type),
            role: plan.role === 'intent-relevant' ? 'intent-relevant' : plan.role === 'required' || !workflowContext.showValidationRack ? 'required' : 'available',
            lifecycle: plan.lifecycle,
            satisfiesDecisionGate: plan.contributesToCalculations ?? false,
            calculationStatus: plan.calculationStatus,
            completesStage: planItemCompletesStage(plan),
            ...(plan.eligibility?.group === 'available-later'
              ? { awaitingPrerequisites: true, awaitingReason: laterLabel(plan), awaitingDetail: plan.eligibility.reason, awaitingReasonCode: plan.eligibility.reasonCode }
              : {}),
            ...(plan.stage ? { stage: plan.stage } : {}),
            ...(plan.auditRecommended
              ? {
                  auditRecommended: true,
                  ...(plan.auditKind ? { auditKind: plan.auditKind } : {}),
                }
              : {}),
            ...(plan.blockingReason ? { blockingReason: plan.blockingReason } : {}),
            ...(analysis ? { analysis } : {}),
            hasRecord: Boolean(analysis || plan.reportId || plan.analysisId),
            ...(workflowEvidence ? { workflowEvidence } : {}),
            ...(plan.primaryAction ? { primaryAction: plan.primaryAction } : {}),
            ...(plan.optionalEvidenceOnly ? { optionalEvidenceOnly: true } : {}),
          };
        }
        return {
          type,
          label: analysisLabel(type),
          role: 'required',
          lifecycle: analysis ? 'available' : 'missing',
          satisfiesDecisionGate: null,
          completesStage: null,
          ...(analysis ? { analysis } : {}),
          hasRecord: Boolean(analysis),
          ...(workflowEvidence ? { workflowEvidence } : {}),
        };
      }),
    };
  }

  return {
    matrixMode: 'available',
    matrixRows: latestAnalyses.map((analysis): AnalysisMatrixRow => ({
      type: analysisType(analysis),
      label: analysisLabel(analysisType(analysis)),
      role: 'available',
      lifecycle: 'available',
      satisfiesDecisionGate: null,
      completesStage: null,
      analysis,
      hasRecord: true,
    })),
  };
}

/** Blocking grounding violations recorded on a revision. */
function revisionBlockCount(report: SiteRagReport): number {
  const violations = (report as unknown as {
    qaFindings?: { grounding?: { violations?: ReadonlyArray<{ severity?: string }> } };
  }).qaFindings?.grounding?.violations ?? [];
  return violations.filter(v => v.severity === 'block').length;
}

/** Quality-hold stamps recorded on a revision. */
function revisionStampCount(report: SiteRagReport): number {
  return ((report as unknown as { qualityHoldFindings?: readonly unknown[] })
    .qualityHoldFindings ?? []).length;
}

/**
 * Why a revision sits in its state, in the reader's language.
 *
 * The bare state word is not enough on its own: "failed" and "held" both read as
 * "something went wrong" while meaning very different things, and a row that
 * says only `held` does not tell an operator whether the analysis is one nit or
 * twenty from being usable.
 */
function revisionReadinessCodes(report: SiteRagReport): Set<string> {
  const reasons = report.analysisRevision?.readiness?.reasons
    ?? report.analysisReadiness?.reasons
    ?? [];
  return new Set(reasons.map(r => r.code));
}

function isIntegrityFailure(codes: ReadonlySet<string>): boolean {
  return codes.has('snapshot-identity-invalid')
    || codes.has('snapshot-schema-invalid')
    || codes.has('current-input-invalid');
}

function isQualityHold(codes: ReadonlySet<string>): boolean {
  return codes.has('hard-gate-failed')
    || codes.has('data-inadequate')
    || codes.has('quality-held')
    || codes.has('quality-failed');
}

/**
 * Chip state for a revision row. Revisions written BEFORE the failed/held
 * split are PERSISTED as `failed` even when they produced a full document
 * (immutable snapshots are never rewritten) — their readiness REASONS record
 * what actually happened. The chip must agree with the cause copy beside it:
 * a produced-but-quality-held analysis reads `held`, never `failed`.
 */
function displayRevisionState(report: SiteRagReport, state: string): string {
  if (state !== 'failed') return state;
  const codes = revisionReadinessCodes(report);
  return isQualityHold(codes) && !isIntegrityFailure(codes) ? 'held' : state;
}

function revisionCause(report: SiteRagReport, state: string): string {
  const codes = revisionReadinessCodes(report);
  if (state === 'ready') return 'Cleared every gate.';
  if (state === 'stale') return 'Superseded — the site evidence changed while this ran.';
  const integrityFailed = isIntegrityFailure(codes);
  /*
   * LEGACY ROWS (2026-08-17). Revisions written before the failed/held split
   * are PERSISTED as `failed` even though they produced a full document — the
   * immutable snapshot is not rewritten. Their readiness REASONS still record
   * what actually happened, so a row whose only reasons are quality reasons is
   * described as the hold it was, not as a run that never produced anything.
   */
  const qualityHeld = isQualityHold(codes);
  if (state === 'failed' && integrityFailed) {
    return 'No trustworthy artifact — the stored analysis failed its own integrity check.';
  }
  if (state === 'failed' && !qualityHeld) {
    return 'No usable analysis was produced.';
  }
  if (state === 'failed' || state === 'held' || state === 'degraded') {
    const parts: string[] = [];
    if (codes.has('hard-gate-failed')) parts.push('a truth gate flagged a statement');
    if (codes.has('data-inadequate')) parts.push('the evidence base is thin');
    if (codes.has('quality-held')) parts.push('the quality review asked for a revision');
    return parts.length > 0
      ? `Analysis produced, held for revision — ${parts.join('; ')}.`
      : 'Analysis produced, held for revision.';
  }
  return '';
}

/**
 * What measurably moved against the PREVIOUS revision.
 *
 * Deltas, not absolutes: a reader scanning six rows tagged the same way needs to
 * see direction of travel. Live on Triple Threat the six revisions carried
 * 20 -> 13 -> 1 -> 4 -> 5 -> 3 quality stamps — real, varying progress rendered
 * as six identical badges.
 *
 * `older` is the chronologically preceding revision; the oldest row gets no
 * deltas because it has nothing to be compared against.
 */
function revisionChanges(
  report: SiteRagReport,
  older: SiteRagReport | undefined,
): string[] {
  if (!older) return [];
  const out: string[] = [];
  const delta = (label: string, now: number, before: number): void => {
    if (now === before) return;
    const direction = now < before ? '↓' : '↑';
    out.push(`${label} ${before} → ${now} ${direction}`);
  };
  delta('Quality stamps', revisionStampCount(report), revisionStampCount(older));
  delta('Grounding blocks', revisionBlockCount(report), revisionBlockCount(older));

  const nowState = report.analysisRevision?.readiness?.state
    ?? report.analysisReadiness?.state;
  const wasState = older.analysisRevision?.readiness?.state
    ?? older.analysisReadiness?.state;
  if (nowState && wasState && nowState !== wasState) {
    out.push(`Readiness ${wasState} → ${nowState}`);
  }
  return out;
}

function collectRevisionHistory(
  site: SiteEntity,
  analyses: readonly SiteRagReport[],
): RevisionHistoryRow[] {
  const ordered = analyses
    .filter(report => (
      (!report.siteId || report.siteId === site.id)
      && Boolean(report.decisionAnalysis || report.analysisRevision)
    ))
    .slice()
    .sort((left, right) => analysisTimestamp(right) - analysisTimestamp(left));

  return ordered.map((report, index) => {
    const semantic = report.decisionAnalysis;
    const reference = report.analysisRevision;
    // Revisions written before readiness was persisted have no state to report.
    // `persistedState` drives the badge (null => no badge); the internal
    // `state` keeps the historical 'legacy' token so cause/variant derivation
    // below behaves exactly as before.
    const persistedState =
      reference?.readiness?.state
      ?? report.analysisReadiness?.state
      ?? null;
    const state = persistedState ?? 'legacy';
    // Sorted newest-first, so the PREVIOUS revision is the next element.
    const older = ordered
      .slice(index + 1)
      .find(candidate => analysisType(candidate) === analysisType(report));
    return {
      key: reference?.analysisId ?? semantic?.analysisId ?? report.id,
      analysis: analysisLabel(analysisType(report)),
      revision: (
        reference?.contentHash
        ?? semantic?.contentHash
        ?? report.reportRevision?.documentHash
        ?? report.id
      ).slice(0, 12),
      createdAt: reference?.createdAt ?? semantic?.createdAt ?? report.createdAt,
      // Chip shows the DISPLAY state (legacy quality-held rows read 'held');
      // the cause derivation keeps the raw persisted state.
      state: persistedState ? displayRevisionState(report, persistedState) : null,
      cause: revisionCause(report, state),
      changes: revisionChanges(report, older),
    };
  });
}

function collectExportHistory(
  site: SiteEntity,
  analyses: readonly SiteRagReport[],
): ExportHistoryRow[] {
  return analyses
    .filter(report => !report.siteId || report.siteId === site.id)
    .flatMap((report) => {
      const artifacts = report.reportRevision?.artifacts ?? {};
      return Object.values(artifacts).flatMap((artifact) => {
        if (!artifact) return [];
        const pageSize = artifact.format === 'pdf'
          ? artifact.pageSize ?? 'A4'
          : null;
        return [{
          key: `${report.id}:${artifact.format}:${pageSize ?? 'native'}:${artifact.sha256}`,
          analysis: analysisLabel(analysisType(report)),
          format: pageSize ? `${artifact.format.toUpperCase()} · ${pageSize}` : artifact.format.toUpperCase(),
          state: artifact.state,
          createdAt: report.updatedAt ?? report.createdAt,
          detail: [
            artifact.pageCount ? `${artifact.pageCount} pages` : null,
            artifact.slideCount ? `${artifact.slideCount} slides` : null,
            `${Math.max(1, Math.round(artifact.byteLength / 1024))} KB`,
          ].filter(Boolean).join(' · '),
        }];
      });
    })
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
}

/**
 * Convert the existing report-shaped compatibility artifacts into a
 * presentation-neutral dossier model. Keeping this pure makes the later
 * canonical analysis-snapshot adapter a small boundary change.
 */
export function buildFindingsDossierModel(input: Pick<
  FindingsDossierProps,
  'site' | 'analyses' | 'pathToGo' | 'requiredAnalysisTypes' | 'sed' | 'generationImpactEnabled' | 'journey'
>): FindingsDossierModel {
  // Lanes read RAW report fields (conclusion.*, recommendations[], risks[],
  // crossReportSynthesis[]). Present them as the report reader does: a leaf
  // the reviewer-voice gate withheld reads "not assessed", and a stored legacy
  // quality-gate sentinel never reaches a lane. Reference-preserving.
  const analyses = presentReportsText(input.analyses);
  const latestAnalyses = latestAnalysesForSite(input.site, analyses, input.pathToGo);
  const planByType = new Map(
    (input.pathToGo?.reportPlan ?? []).map(item => [item.type, item] as const),
  );
  const calculationAnalyses = latestAnalyses.filter(report => {
    const type = analysisType(report);
    const plan = planByType.get(type);
    if (plan?.decisionEvidenceSource === 'semantic-analysis') {
      return report.decisionAnalysis?.analysisId === plan.analysisId
        || report.analysisRevision?.analysisId === plan.analysisId;
    }
    if (plan?.calculationReportId) return report.id === plan.calculationReportId;
    return reportCanContributeSavedResult(report, type);
  });
  const findings: FindingCandidate[] = [];
  const risks: FindingCandidate[] = [];
  const gaps: FindingCandidate[] = [];
  const conditions: FindingCandidate[] = [];

  for (const report of calculationAnalyses) {
    const semantic = report.decisionAnalysis?.semantic;
    for (const finding of semantic?.findings ?? []) {
      addCandidate(
        polarityFromImpact(finding.impact) === 'negative' ? risks : findings,
        report,
        finding.title,
        { detail: finding.detail, severity: finding.impact, titleKind: 'headline' },
      );
    }
    for (const risk of semantic?.risks ?? []) {
      addCandidate(risks, report, risk.label, {
        detail: `${risk.trigger} Mitigation: ${risk.mitigation}`,
        severity: risk.severity,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const constraint of semantic?.bindingConstraints ?? []) {
      addCandidate(risks, report, constraint.label, {
        detail: constraint.detail,
        severity: constraint.effect,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const gap of semantic?.gaps ?? []) {
      const resolved = resolveGapPresentation(
        report,
        gap.field,
        `${gap.reason} Next step: ${gap.recommendedAction}`,
      );
      addCandidate(gaps, report, resolved.title, {
        detail: resolved.detail,
        severity: gap.severity,
        titleKind: 'headline',
      });
    }
    for (const unknown of semantic?.unknowns ?? []) {
      const resolved = resolveGapPresentation(report, unknown.field, unknown.reason);
      addCandidate(gaps, report, resolved.title, {
        detail: resolved.detail,
        severity: `unknown · ${unknown.materiality}`,
      });
    }
    for (const conflict of semantic?.conflicts ?? []) {
      const resolved = resolveGapPresentation(report, conflict.field, conflict.description);
      addCandidate(gaps, report, resolved.title, {
        detail: resolved.detail,
        severity: `${conflict.status} conflict · ${conflict.severity}`,
      });
    }
    for (const condition of semantic?.conditions ?? []) {
      addCandidate(conditions, report, condition.label, {
        detail: condition.requirement,
        severity: condition.status,
        titleKind: 'headline',
      });
    }

    for (const finding of report.structuredFindings ?? []) {
      // `structuredFindings[].impact` is typed `string` and carries either the
      // enum or a full impact sentence, so the route reads it through
      // `polarityFromImpact` rather than testing `=== 'negative'`. Without this
      // every negative screening chip landed in the lane captioned
      // "Strengths, opportunities, and cross-domain conclusions".
      addCandidate(
        polarityFromImpact(finding.impact) === 'negative' ? risks : findings,
        report,
        finding.title,
        { detail: finding.detail, severity: finding.impact, titleKind: 'headline' },
      );
    }
    for (const finding of report.contextualKeyFindings ?? []) {
      const target = polarityFromImpact(finding.impact) === 'negative' ? risks : findings;
      addCandidate(target, report, finding.title, {
        detail: finding.detail || finding.whyKey,
        severity: finding.impact,
        titleKind: 'headline',
      });
    }
    for (const strength of readReportStrengths(
      report,
      Number.POSITIVE_INFINITY,
      { truncateAt: 'none' },
    ).items) {
      addCandidate(findings, report, strength, { fieldPolarity: 'positive' });
    }
    for (const synthesis of report.crossReportSynthesis ?? []) {
      addCandidate(findings, report, synthesis.domain, {
        detail: synthesis.synthesis,
        titleKind: 'headline',
      });
    }

    for (const risk of readConclusionRiskItems(
      report,
      { truncateAt: 'none' },
    ).items) {
      addCandidate(risks, report, risk, { fieldPolarity: 'negative' });
    }
    for (const risk of report.risks ?? []) {
      addCandidate(risks, report, risk.label, {
        detail: [risk.impact, risk.mitigation ? `Mitigation: ${risk.mitigation}` : '']
          .filter(Boolean)
          .join(' '),
        severity: risk.severity,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const risk of report.executiveSummaryCriticalRisks ?? []) {
      addCandidate(risks, report, risk.risk, {
        detail: risk.mitigation ? `Mitigation: ${risk.mitigation}` : undefined,
        severity: risk.severity,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const risk of report.customBriefExecutiveSummary?.materialRisks ?? []) {
      addCandidate(risks, report, risk.claim, {
        detail: risk.openQuestion || risk.suggestedFollowUp,
        severity: risk.evidenceState,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const risk of report.customBriefConclusion?.keyRisks ?? []) {
      addCandidate(risks, report, risk.claim, {
        detail: risk.openQuestion || risk.suggestedFollowUp,
        severity: risk.evidenceState,
        titleKind: 'headline',
        fieldPolarity: 'negative',
      });
    }
    for (const risk of report.interconnectionAnalysis?.risks ?? []) {
      addCandidate(risks, report, risk, { fieldPolarity: 'negative' });
    }

    for (const gap of report.dataGaps ?? []) {
      addCandidate(gaps, report, gap.field, {
        detail: `${gap.reason} Next step: ${gap.recommendation}`,
        severity: gap.impact,
      });
    }
    for (const gap of report.sedGaps ?? []) {
      addCandidate(gaps, report, gap);
    }
    for (const category of report.categories ?? []) {
      for (const metric of category.metrics ?? []) {
        if (!metric.missing) continue;
        const resolved = resolveGapPresentation(
          report,
          metric.label,
          `${category.label}: ${metric.explanation || 'Evidence is not specified.'}`,
        );
        addCandidate(gaps, report, resolved.title, {
          detail: resolved.detail,
          severity: 'missing evidence',
        });
      }
    }
    if (report._metadataOnly) {
      addCandidate(gaps, report, 'Full analysis body is unavailable', {
        detail: 'Only list metadata was loaded. Reload the analysis before relying on its findings.',
        severity: 'data availability',
      });
    }

    for (const condition of report.conclusion?.keyConditions ?? []) {
      addCandidate(conditions, report, condition);
    }

  }

  for (const item of input.pathToGo?.reportPlan ?? []) {
    if (item.role !== 'required' || planItemCompletesStage(item)) continue;
    const report = latestAnalyses.find(candidate => (
      candidate.id === item.calculationReportId
      || analysisType(candidate) === item.type
    ));
    if (!report) continue;
    // No severity tag: it printed the raw lifecycle code ("QUALITY-HELD",
    // "REFRESH-NEEDED", …) as a badge (owner, 2026-09-30). A code is not a
    // severity — it never set the row's polarity or colour either.
    addCandidate(gaps, report, `${analysisLabel(item.type)} has no usable successful result`, {
      detail: item.blockingReason ?? LIFECYCLE_LABEL[item.lifecycle],
    });
  }

  // Merge each lane's near-duplicate statements into claim clusters. Every
  // contributing statement survives inside its cluster; disagreeing members
  // are stamped rather than reconciled.
  const lanes = {
    findings: clusterFindings(findings),
    risks: clusterFindings(risks),
    gaps: clusterFindings(gaps),
    conditions: clusterFindings(conditions),
  };

  const executive = calculationAnalyses.find(
    report => analysisType(report) === 'executive-summary',
  );
  const decisionSummary = (
    executive?.btmViabilityAssessment
    || executive?.btmDecisionSummary
    || calculationAnalyses.find(report => report.btmDecisionSummary)?.btmDecisionSummary
    || ''
  ).trim();

  return {
    latestAnalyses,
    ...buildMatrixRows(
      latestAnalyses,
      input.pathToGo,
      input.requiredAnalysisTypes,
      {
        site: input.site,
        sed: input.sed,
        generationImpactEnabled: input.generationImpactEnabled,
        showValidationRack: Boolean(input.journey),
      },
      analyses,
    ),
    ...lanes,
    screeningContradictions: detectScreeningContradictions([
      lanes.findings,
      lanes.risks,
      lanes.gaps,
      lanes.conditions,
    ]),
    revisionHistory: collectRevisionHistory(input.site, analyses),
    exportHistory: collectExportHistory(input.site, analyses),
    ...(decisionSummary ? { decisionSummary } : {}),
  };
}

function readableDate(value: string | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

interface ReadinessCheckpoint {
  id: string;
  label: string;
  detail: string;
  complete: boolean;
  pending: boolean;
  /** A run is actively mutating this gate (e.g. an analysis mid-generation). */
  running?: boolean;
}

/**
 * TWO-STAGE cockpit (owner, 2026-09-06). One line per stage in the stages'
 * own vocabulary — "current" counts, the screening result and score, the
 * scenario gate — never a band word while the verdict is withheld.
 */
function stageLine(stage: DecisionStageRollup): string {
  const count = `${stage.qualifiedTypes.length} of ${stage.requiredTypes.length} used`;
  if (stage.stage === 1) {
    const result = stage.complete && stage.outcome
      ? `screened · ${stage.outcome}${stage.score != null ? ` · ${Math.round(stage.score)}/100` : ''}`
      : stage.score != null ? `provisional score ${Math.round(stage.score)}/100` : null;
    return `Stage 1 · ${result ? `${result} · ${count}` : count}`;
  }
  if (stage.requiredTypes.length === 0) return 'Stage 2 · nothing required';
  return `Stage 2 · ${stage.locked ? `scenario needed · ${count}` : count}`;
}

function stagesLine(stages: DecisionStagesRollup): string {
  return `${stageLine(stages.screening)} · ${stageLine(stages.validation)}`;
}

/** What still withholds the band, in stage vocabulary — null once both stages are complete. */
function stageHoldDetail(stages: DecisionStagesRollup): string | null {
  const { screening, validation } = stages;
  if (!screening.complete) {
    return `Early screening incomplete · ${screening.qualifiedTypes.length} of ${screening.requiredTypes.length} used`;
  }
  if (validation.locked) return 'Mature validation needs a promoted generation scenario';
  if (!validation.complete) {
    return `Mature validation incomplete · ${validation.qualifiedTypes.length} of ${validation.requiredTypes.length} used`;
  }
  return null;
}

function readinessCheckpoints(
  model: FindingsDossierModel,
  verdict: DerivedSiteVerdict | null | undefined,
  pathToGo: PathToGoState | null | undefined,
): ReadinessCheckpoint[] {
  const requiredRows = model.matrixRows.filter(row => row.role === 'required');
  const requiredUsed = requiredRows.filter(row => row.completesStage === true).length;
  const requiredCurrent = requiredRows.filter(row => row.calculationStatus === 'current').length;
  const preDecisionPhases = pathToGo?.phases.filter(phase => phase.id !== 'decide') ?? [];
  const clearedPhases = preDecisionPhases.filter(phase => phase.status === 'cleared').length;
  const adequate = pathToGo?.adequacyGate.adequate ?? verdict?.dataAdequate ?? false;
  const analysisComplete = requiredRows.length > 0
    ? requiredUsed === requiredRows.length
    : model.latestAnalyses.length > 0;
  const verificationComplete = preDecisionPhases.length > 0
    ? clearedPhases === preDecisionPhases.length
    : false;
  const coverageRatioForDetail =
    pathToGo?.adequacyGate.coverageRatio ?? verdict?.coverageRatio ?? null;

  return [
    {
      id: 'evidence',
      label: 'Evidence basis',
      // The verdict lede ALREADY states the adequacy reason — echoing it here
      // put the same sentence on screen twice (reported live 2026-08-05, and
      // the exact duplication the verdict strip was meant to remove). When the
      // gate is failing for the reason the lede just gave, say something the
      // lede cannot: the measured coverage and the source count. Only fall
      // back to the reason text when there is no coverage figure to show.
      detail: adequate
        ? (coverageRatioForDetail != null
            ? `Adequacy gate cleared · ${Math.round(coverageRatioForDetail * 100)}% pillar coverage`
            : 'Adequacy gate cleared')
        // P2.3 — under a withheld lede the pillar-coverage percentage is a
        // second, differently-measured number next to the one the lede just
        // gave ("Only 60% of decision weight is measured" beside "78% pillar
        // coverage"). Say what the lede cannot — how many sources exist and
        // that the verdict is withheld — without a competing figure.
        : coverageRatioForDetail != null
          ? `${model.latestAnalyses.length} source${model.latestAnalyses.length === 1 ? '' : 's'} on file · verdict withheld (see above)`
          : (
              pathToGo?.adequacyGate.reason
              ?? verdict?.adequacyReason
              ?? 'Evidence adequacy has not been established'
            ),
      complete: adequate,
      pending: !adequate && model.latestAnalyses.length > 0,
    },
    {
      id: 'analysis',
      label: 'Required analyses',
      detail: pathToGo?.decisionRollup?.stages
        ? stagesLine(pathToGo.decisionRollup.stages)
        : requiredRows.length > 0
        ? `${requiredUsed} of ${requiredRows.length} used${requiredCurrent < requiredUsed ? ` · ${requiredCurrent} current` : ''}`
        : (
            model.latestAnalyses.length > 0
              ? `${model.latestAnalyses.length} analysis record${model.latestAnalyses.length === 1 ? '' : 's'} available`
              : 'No analysis records available'
          ),
      complete: analysisComplete,
      pending: !analysisComplete && model.latestAnalyses.length > 0,
    },
    {
      id: 'verification',
      label: 'Verification',
      detail: preDecisionPhases.length > 0
        ? `${clearedPhases} of ${preDecisionPhases.length} prerequisite phases cleared`
        : 'Decision verification is not connected',
      complete: verificationComplete,
      pending: preDecisionPhases.length > 0 && !verificationComplete,
    },
  ];
}

function readinessPresentation(
  verdict: DerivedSiteVerdict | null | undefined,
  pathToGo: PathToGoState | null | undefined,
): {
  label: string;
  detail: string;
  variant: StatusPillVariant;
  icon: LucideIcon;
} {
  // A stage hold outranks every other reading: while the rollup withholds the
  // band, the cockpit never says "ready" — it names the stage, in the stages'
  // own vocabulary (one vocabulary for a withheld decision; never a band word).
  const stageHold = pathToGo?.decisionRollup && pathToGo.decisionRollup.band == null && pathToGo.decisionRollup.stages
    ? stageHoldDetail(pathToGo.decisionRollup.stages)
    : null;
  if (pathToGo && stageHold) {
    return {
      label: VERDICT_WITHHELD_LABEL,
      detail: stageHold,
      variant: 'neutral',
      icon: Clock3,
    };
  }
  if (pathToGo && pathToGoPositiveDecisionAllowed(pathToGo)) {
    return {
      label: 'Decision evidence ready',
      detail: 'Usable successful results are available for the required analyses and verification record.',
      variant: 'success',
      icon: ShieldCheck,
    };
  }
  if (pathToGo && !pathToGo.adequacyGate.adequate) {
    return {
      label: VERDICT_WITHHELD_LABEL,
      detail:
        pathToGo.adequacyGate.reason
        ?? verdict?.adequacyReason
        ?? 'Evidence adequacy must be resolved before a positive decision.',
      variant: 'neutral',
      icon: ShieldAlert,
    };
  }
  if (pathToGo) {
    return {
      label: 'Decision record in progress',
      detail: 'Review the open analysis and verification items below.',
      variant: 'warn',
      icon: Clock3,
    };
  }
  if (verdict?.dataAdequate) {
    return {
      label: 'Decision signal available',
      detail: 'Connect the decision requirements to validate the full positive-decision gate.',
      variant: bandStatusPillVariant(verdict.bandColor),
      icon: Gauge,
    };
  }
  return {
    label: VERDICT_WITHHELD_LABEL,
    detail:
      verdict?.adequacyReason
      ?? 'Add evidence and analyses to establish a decision signal.',
    variant: 'neutral',
    icon: CircleDashed,
  };
}

/* Editorial apparatus (2026-08-17) — the readiness spine sheds its boxed
   tiles and icon roundels for the reports' front-matter vocabulary: three
   hairline-separated columns headed by roman-numeral small-caps phase
   labels in status ink. The status glyph survives, unboxed and inline,
   so running/pending states stay legible at a glance. */
const ReadinessSpine: React.FC<{
  checkpoints: ReadinessCheckpoint[];
}> = ({ checkpoints }) => (
  <ol
    className="grid md:grid-cols-3"
    aria-label="Decision readiness checkpoints"
    data-testid="findings-readiness-spine"
  >
    {checkpoints.map((checkpoint, index) => {
      const statusInk = checkpoint.complete
        ? 'text-success'
        : checkpoint.running
          ? 'text-primary'
          : checkpoint.pending
            ? 'text-warning'
            : 'text-text-muted';
      const StatusGlyph = checkpoint.complete
        ? CheckCircle2
        : checkpoint.running
          ? RefreshCcw
          : checkpoint.pending
            ? Clock3
            : CircleDashed;
      return (
        <li
          key={checkpoint.id}
          className={cn(
            'min-w-0 py-2 md:pr-5',
            index > 0 && 'border-t border-border-subtle md:border-l md:border-t-0 md:pl-5',
          )}
          data-testid={`findings-checkpoint-${checkpoint.id}`}
          data-complete={checkpoint.complete ? 'true' : 'false'}
        >
          <p className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em]">
            <span className={statusInk}>{['I', 'II', 'III'][index] ?? index + 1}</span>
            <span className="text-text-muted" aria-hidden>·</span>
            <span className="text-text-primary">{checkpoint.label}</span>
            <StatusGlyph
              className={cn(
                'h-3 w-3 self-center',
                statusInk,
                checkpoint.running && 'animate-pulse motion-reduce:animate-none',
              )}
              aria-hidden
            />
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-text-muted">
            {checkpoint.detail}
          </p>
        </li>
      );
    })}
  </ol>
);

/**
 * OPTIONAL ANALYSES — the report types the decision matrix does not track.
 *
 * The matrix lists REQUIRED analyses (from Path to GO) plus whatever already
 * exists. Types that are neither — Custom Brief above all — appeared nowhere on
 * this tab, and the Reports-tab launcher that used to reach them is gated on
 * `reportTypeCount > 3` or an empty site. A site with one to three report types
 * therefore had NO route to start a new type at all: fully built report types,
 * with prompts, schemas and wizards, unreachable from the product.
 *
 * These are optional by design, not by oversight — `COVERAGE_META_REPORT_TYPES`
 * excludes them from every readiness surface because a user-defined brief has
 * nothing a coverage gate could evaluate. That is a reason to list them apart
 * from the decision basis, not a reason to hide them.
 */
/**
 * The one DECISION-MATERIAL additional analysis. Since the owner's 2026-09-30
 * move Public Sentiment is listed under "Additional analyses", but a saved
 * report still feeds the social pillar and a present red or amber one caps the
 * band at CONDITIONAL — so its freshness and failure state stay disclosed here
 * as its stage row disclosed them: the lifecycle pill, "Using saved successful
 * result", the explained refresh error, and the stall warning with Cancel on a
 * running refresh. A green tick only for a current result. The other
 * additional analyses (advisory Connectivity, unscored briefs) keep the plain
 * Optional row.
 */
const STATUS_DISCLOSED_ADDITIONAL: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>(['public-sentiment']);

const OptionalAnalyses: React.FC<{
  analyses: readonly SiteRagReport[];
  matrixRows: AnalysisMatrixRow[];
  planItems?: readonly ReportPlanItem[];
  isDatacrateAdmin: boolean;
  onOpenAnalysis?: (analysisId: string) => void;
  onLaunchAnalysis?: (analysisType: ReportTypeId) => void;
  onPlanAction?: PlanActionHandler;
  inflightByType?: Partial<Record<ReportTypeId, { progress?: number; progressStage?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>>;
  /**
   * Public Sentiment is listed here since the 2026-09-30 stage move, so its
   * evidence notices travel with it: a run waiting for a site-evidence build
   * (with Cancel) and a held run (Rebuild SED / Run again), exactly as the
   * decision board rendered them.
   */
  siteId?: string;
  refreshProgressByType?: FindingsDossierProps['refreshProgressByType'];
  onCancelRun?: (runId: string) => void;
  /** Failure and auto-refresh state for a STATUS_DISCLOSED_ADDITIONAL row. */
  refreshErrorByType?: FindingsDossierProps['refreshErrorByType'];
  autoRebindByType?: FindingsDossierProps['autoRebindByType'];
}> = ({
  analyses,
  matrixRows,
  planItems = [],
  isDatacrateAdmin,
  onOpenAnalysis,
  onLaunchAnalysis,
  onPlanAction,
  inflightByType,
  siteId,
  refreshProgressByType,
  onCancelRun,
  refreshErrorByType,
  autoRebindByType,
}) => {
  const sentimentEvidenceHold = useSentimentEvidenceHold(siteId);
  const trackedTypes = new Set(matrixRows.map(row => row.type));
  const planByType = new Map(planItems.map(item => [item.type, item] as const));
  // Latest existing analysis per untracked type, so a Custom Brief that HAS
  // been generated is openable here rather than only launchable again.
  const existingByType = new Map<ReportTypeId, SiteRagReport>();
  for (const analysis of analyses) {
    const type = analysisType(analysis);
    if (trackedTypes.has(type)) continue;
    const current = existingByType.get(type);
    if (!current || analysisTimestamp(analysis) > analysisTimestamp(current)) {
      existingByType.set(type, analysis);
    }
  }
  const rows = getOfferableReportTypes(isDatacrateAdmin)
    .filter(type => !trackedTypes.has(type.id))
    .map(type => {
      const plan = planByType.get(type.id);
      // A status-disclosed row also runs while the PLAN says so (a server-side
      // refresh the generation-job store does not track), as its stage row did.
      const planRunning = STATUS_DISCLOSED_ADDITIONAL.has(type.id)
        && (plan?.lifecycle === 'queued' || plan?.lifecycle === 'generating');
      const progress = refreshProgressByType?.[type.id];
      return {
        type,
        existing: existingByType.get(type.id),
        running: inflightByType?.[type.id] ?? (planRunning
          ? { progress: progress?.progress, progressStage: progress?.stage, startedAt: progress?.startedAt, estimatedDurationMs: progress?.estimatedDurationMs }
          : undefined),
        plan,
      };
    });
  const laterRows = rows.filter(row => row.plan?.eligibility?.group === 'available-later');
  const optionalRows = rows.filter(row => row.plan?.eligibility?.group !== 'available-later');

  // One-line rows on the shared stage grid: name · one line of why · state · one action.
  const renderRows = (
    entries: typeof rows,
    lane: 'available-later' | 'optional',
  ) => (
    <ol className="@container list-none p-0">
      {entries.map(({ type, existing, running, plan }) => {
        const planAction = plan?.eligibility?.nextAction ?? plan?.primaryAction;
        const canPrepare = lane === 'available-later'
          && planAction != null
          && planAction.kind !== 'none'
          && onPlanAction != null;
        const why = lane === 'available-later'
          ? (type.id === 'financial'
              ? 'Complete the financial model first'
              : type.id === 'supply-chain-risk'
                ? 'Choose equipment first'
                : plan?.eligibility?.reason ?? type.description)
          : type.description;
        const isSentiment = type.id === 'public-sentiment';
        const sentimentProgress = isSentiment ? refreshProgressByType?.[type.id] : undefined;
        // A Public Sentiment run waiting for a site-evidence build is queued,
        // not analysing: a waiting notice with Cancel, no countdown.
        const waitingForEvidence = Boolean(running) && isSentiment
          && isSentimentEvidenceWaitStage(sentimentProgress?.stage ?? running?.progressStage);
        const evidenceHold = !running && isSentiment ? sentimentEvidenceHold : null;
        // Decision-material additional analysis: its plan lifecycle is shown.
        const disclosed = lane === 'optional' && STATUS_DISCLOSED_ADDITIONAL.has(type.id) && plan != null && existing != null;
        const truthQuestionCount = sharedTruthQuestionCountOf(existing);
        const autoRefreshExhausted = autoRebindByType?.[type.id]?.exhausted ?? false;
        const presentation = disclosed && plan
          ? lifecyclePresentation(plan.lifecycle, truthQuestionCount, autoRefreshExhausted)
          : null;
        const notCurrent = disclosed && plan != null && (plan.lifecycle !== 'current' || plan.stale);
        const glyphKind = running
          ? 'running'
          : existing
            ? notCurrent
              ? presentation?.tone === 'danger' ? 'danger' : 'warn'
              : 'current'
            : lane === 'available-later' ? 'later' : 'ready';
        return (
          <StageRow
            key={type.id}
            glyph={<StageGlyph kind={glyphKind} data-testid={disclosed ? `findings-optional-glyph-${type.id}` : undefined} />}
            main={(
              <>
                <StageRowName>{type.label}</StageRowName>
                <StageRowMeta title={why}>{why}</StageRowMeta>
              </>
            )}
            state={running ? (
              <div
                className="flex min-w-0 flex-col items-start gap-1"
                data-testid={`findings-optional-progress-${type.id}`}
                title={running.progressStage || undefined}
              >
                <AnalysisLifecycleStatusPill
                  lifecycle="generating"
                  labelOverride={waitingForEvidence ? 'Waiting for evidence' : (
                    <GenerationCountdown
                      startedAt={running.startedAt}
                      estimatedDurationMs={running.estimatedDurationMs}
                      fallback={running.progressStage ? 'In progress' : 'Starting…'}
                      testId={`findings-optional-countdown-${type.id}`}
                    />
                  )}
                />
                {waitingForEvidence ? (
                  <SentimentEvidenceWaitingNotice
                    runId={sentimentProgress?.runId}
                    onCancelRun={onCancelRun}
                    className="max-w-[16rem]"
                    data-testid={`findings-optional-evidence-wait-${type.id}`}
                  />
                ) : (
                  <>
                    <div className="h-1 w-32 overflow-hidden rounded-sm bg-background-elevated">
                      <div
                        className="h-full rounded-sm bg-primary transition-[width] duration-700"
                        style={{ width: `${Math.max(4, Math.min(100, running.progress ?? 4))}%` }}
                      />
                    </div>
                    {STATUS_DISCLOSED_ADDITIONAL.has(type.id) && refreshProgressByType?.[type.id] ? (
                      <AnalysisRunHealth
                        rowType={type.id}
                        progress={refreshProgressByType[type.id]}
                        onCancelRun={onCancelRun}
                      />
                    ) : null}
                  </>
                )}
              </div>
            ) : lane === 'optional' ? (
              // Only optional rows carry a tag: an available-later row has no
              // "Later" tag (owner, 2026-09-30) — the section heading and the
              // row's reason line already say it.
              evidenceHold || notCurrent ? (
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <span className="type-meta text-text-muted">Optional</span>
                  {notCurrent && plan && plan.lifecycle !== 'refresh-needed' && presentation?.label != null ? (
                    <AnalysisLifecycleStatusPill
                      lifecycle={plan.lifecycle}
                      truthQuestionCount={truthQuestionCount}
                      autoRefreshExhausted={autoRefreshExhausted}
                      data-testid={`findings-optional-lifecycle-${type.id}`}
                    />
                  ) : null}
                  {notCurrent && plan && !evidenceHold ? (
                    <p className="max-w-[22rem] text-xs leading-relaxed text-text-secondary" data-testid={`findings-optional-reason-${type.id}`}>
                      {plan.blockingReason ?? (plan.lifecycle === 'refresh-needed' || plan.stale
                        ? 'Report inputs changed. Open the saved report to review what needs updating.'
                        : plan.lifecycle === 'failed'
                          ? 'The latest run did not complete. Open the saved report or review the run error.'
                          : 'Report checks need attention. Open the saved report to review them.')}
                    </p>
                  ) : null}
                  {notCurrent && (plan?.lifecycle === 'failed' || autoRefreshExhausted) ? (
                    <RefreshErrorNote
                      error={refreshErrorByType?.[type.id]}
                      truthQuestionCount={truthQuestionCount}
                      autoRebind={autoRebindByType?.[type.id]}
                      data-testid={`findings-optional-refresh-error-${type.id}`}
                    />
                  ) : null}
                  {evidenceHold ? (
                    <SentimentEvidenceHoldNotice
                      hold={evidenceHold}
                      className="max-w-[16rem]"
                      data-testid={`findings-optional-evidence-hold-${type.id}`}
                    />
                  ) : null}
                </div>
              ) : (
                <span className="type-meta text-text-muted">Optional</span>
              )
            ) : null}
            action={running ? (
              // A refreshing decision-material report stays readable, as its
              // stage row kept it.
              STATUS_DISCLOSED_ADDITIONAL.has(type.id) && existing && onOpenAnalysis ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => onOpenAnalysis(existing.id)} data-testid={`findings-optional-open-${type.id}`}>
                  Open
                </Button>
              ) : null
            ) : (
              <>
                {existing && onOpenAnalysis ? (
                  <Button type="button" size="sm" variant="ghost" onClick={() => onOpenAnalysis(existing.id)} data-testid={`findings-optional-open-${type.id}`}>
                    Open
                  </Button>
                ) : null}
                {canPrepare ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onPlanAction?.(planAction!, type.id)}
                    data-testid={`findings-later-action-${type.id}`}
                    data-action-kind={planAction?.kind}
                  >
                    {type.id === 'financial'
                      ? 'Open financial model'
                      : type.id === 'supply-chain-risk'
                        ? 'Open equipment plan'
                        : planAction?.label}
                    <ArrowUpRight aria-hidden />
                  </Button>
                ) : onLaunchAnalysis ? (
                  // An EXISTING optional analysis keeps its launch control ("Run
                  // again"), matching the in-product guide (reportGuideSteps.ts).
                  <Button type="button" size="sm" variant="outline" onClick={() => onLaunchAnalysis(type.id)} data-testid={`findings-optional-launch-${type.id}`}>
                    {existing ? 'Run again' : 'Start'}
                  </Button>
                ) : null}
              </>
            )}
            data-testid={lane === 'available-later'
              ? `findings-later-row-${type.id}`
              : `findings-optional-row-${type.id}`}
          />
        );
      })}
    </ol>
  );

  return (
    <div data-testid="findings-optional-analyses" className="rounded-lg border border-border bg-card shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border-subtle px-4 py-3 md:px-5">
        <div>
          <p className="type-meta uppercase tracking-[0.14em] text-primary">Additional analyses</p>
          <h2 className="mt-1 type-section text-text-primary">Follow the project basis</h2>
        </div>
        <span className="text-xs text-text-muted">Requirements follow project intent</span>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-text-muted md:px-5" data-testid="findings-optional-empty">
          Every available analysis is already tracked in the decision basis.
        </p>
      ) : (
        <div>
          {laterRows.length > 0 ? (
            <section aria-labelledby="findings-available-later-title" data-testid="findings-available-later">
              <h3 id="findings-available-later-title" className="flex items-baseline justify-between gap-3 border-b border-border-subtle bg-surface-2/60 px-3.5 py-1.5 type-meta uppercase tracking-[0.13em] text-text-secondary">
                <span>Prepare inputs</span>
                <span className="normal-case tracking-normal text-text-muted">Prepare the prerequisites first</span>
              </h3>
              {renderRows(laterRows, 'available-later')}
            </section>
          ) : null}
          {optionalRows.length > 0 ? (
            <section aria-labelledby="findings-optional-title">
              <h3 id="findings-optional-title" className="border-y border-border-subtle bg-surface-2/60 px-3.5 py-1.5 type-meta uppercase tracking-[0.13em] text-text-secondary">
                Optional
              </h3>
              {renderRows(optionalRows, 'optional')}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};

/**
 * A row slot whose children all rendered nothing is no slot at all — the
 * shared row grid then leaves the cell out instead of an empty box and its gap.
 */
function slotOrNull(slot: React.ReactElement<{ children?: React.ReactNode }>): React.ReactElement | null {
  return React.Children.toArray(slot.props.children).length > 0 ? slot : null;
}

/**
 * The strip's decision segment from the verdict every Findings surface reads.
 * Undefined until a band is published: the segment then carries no status
 * badge (owner, 2026-09-30 — no "Pending" chip), only its footnote.
 */
function stripDecisionFor(verdict: DerivedSiteVerdict | null | undefined): StageStripDecision | undefined {
  if (!verdict || verdict.band == null) return undefined;
  const tone: StatusPillVariant = verdict.bandColor === 'green'
    ? 'success'
    : verdict.bandColor === 'amber'
      ? 'warn'
      : verdict.bandColor === 'orange'
        ? 'conditional-high'
        : verdict.bandColor === 'red'
          ? 'danger'
          : 'neutral';
  return { published: true, label: verdict.bandLabel, tone };
}

/** Collection versions are disclosure. Only the canonical plan can establish
 * current consumed inputs or recommend a rerun for material staleness. */
function screeningSedCurrencyLine(
  report: SiteRagReport,
  currentVersion?: number | null,
  planItems?: readonly ReportPlanItem[] | null,
  options?: { omitRerunRecommended?: boolean },
): string {
  const valid = (version: unknown): version is number => typeof version === 'number'
    && Number.isInteger(version) && version > 0;
  const used = report.sedVersionUsed;
  const reportLine = valid(used) ? `Report used SED v${used}` : 'Report SED version not recorded';
  const currentLine = valid(currentVersion) ? `Current SED v${currentVersion}` : 'Current SED version unavailable';
  const currency = planItems?.find(item => item.type === analysisType(report)
    && (item.reportId === report.id || item.calculationReportId === report.id));
  const heldForQualityReview = Boolean(
    currency?.lifecycle === 'quality-held'
      && currency.reportId === report.id
      && (report.analysisReadiness?.state === 'held' || report.analysisRevision?.readiness?.state === 'held'),
  );
  const state = valid(used) && valid(currentVersion)
    ? currency?.lifecycle === 'current' && currency.calculationStatus === 'current'
      ? ' · Inputs unchanged · no rerun needed'
      : currency?.lifecycle === 'refresh-needed' && currency.stale
        ? (options?.omitRerunRecommended ? '' : ' · Rerun recommended')
      : currentVersion > used ? ' · Newer SED available'
      : currentVersion === used
        ? ` · SED version matches${heldForQualityReview ? ' · Held for quality review' : ''}`
        : ' · Version mismatch'
    : '';
  return `${reportLine} · ${currentLine}${state}`;
}

const AnalysisMatrix: React.FC<{
  model: FindingsDossierModel;
  journey?: FindingsDossierProps['journey'];
  composite?: SiteComposite | null;
  onRunNextAction?: FindingsDossierProps['onRunNextAction'];
  /** The plan behind the rows — drives the stage strip and the scenario step. */
  planItems?: readonly ReportPlanItem[] | null;
  /** The Decision Guide's stage rollup — carries the screening result. */
  stages?: DecisionStagesRollup | null;
  decision?: StageStripDecision;
  focusAnalysisId?: string | null;
  onFocusAnalysisHandled?: () => void;
  onOpenAnalysis?: (analysisId: string) => void;
  onResolveInputs?: (analysisType: ReportTypeId) => void;
  onPlanAction?: PlanActionHandler;
  refreshErrorByType?: Partial<Record<ReportTypeId, string>>;
  autoRebindByType?: Partial<Record<ReportTypeId, { exhausted: boolean; streak?: number; optionalEvidenceSections?: string[] }>>;
  refreshProgressByType?: Partial<Record<ReportTypeId, { progress?: number; stage?: string; heartbeatAt?: string | null; runId?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>>;
  onCancelRun?: (runId: string) => void;
  /** Version from this site's current published SED pointer; never a report/live snapshot guess. */
  currentSedVersion?: number | null;
  /** Site whose Public Sentiment site-evidence hold (if any) the row explains. */
  siteId?: string;
  /** Render the "Path to decision" / "Do this next" section above the rows. Off on the Analyses tab. */
  showDecisionPath?: boolean;
}> = ({ model, journey, composite, onRunNextAction, showDecisionPath = true, planItems, stages, decision, focusAnalysisId, onFocusAnalysisHandled, onOpenAnalysis, onResolveInputs, onPlanAction, refreshErrorByType, autoRebindByType, refreshProgressByType, onCancelRun, siteId, currentSedVersion }) => {
  // Public Sentiment held because the site's evidence needs a rebuild: not a
  // failure, so the row explains it (with Rebuild SED) instead of "Failed".
  const sentimentEvidenceHold = useSentimentEvidenceHold(siteId);
  // Row expansion: the per-analysis card renders inside the row it describes.
  // Expandability doubles as a status signal — rows with no persisted analysis
  // have no chevron.
  const [expandedType, setExpandedType] = useState<ReportTypeId | null>(null);
  useEffect(() => {
    if (!focusAnalysisId) return;
    const semanticId = focusAnalysisId.startsWith('analysis-')
      ? focusAnalysisId.slice('analysis-'.length)
      : focusAnalysisId;
    const row = model.matrixRows.find(candidate => (
      candidate.analysis?.id === focusAnalysisId
      || candidate.analysis?.decisionAnalysis?.analysisId === semanticId
      || candidate.analysis?.analysisRevision?.analysisId === semanticId
    ));
    if (!row) return;

    if (row.analysis && analysisForReport(row.analysis) !== null) {
      setExpandedType(row.type);
    }
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`findings-analysis-${row.type}`)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
      onFocusAnalysisHandled?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusAnalysisId, model.matrixRows, onFocusAnalysisHandled]);
  const openRows = model.matrixRows.filter(row => row.completesStage !== true);
  const usedRows = model.matrixRows.filter(row => row.completesStage === true);
  const usesSavedBasis = usedRows.some(row => row.calculationStatus === 'saved');
  // A queued/generating analysis is ALREADY running, and one whose own inputs
  // are absent cannot be started at all: neither is work the reader can pick
  // up. Path to GO › Analyse excludes both the same way, so the two surfaces
  // report one number for one plan.
  const startableRows = openRows.filter(
    row => row.lifecycle !== 'queued'
      && row.lifecycle !== 'generating'
      && !row.awaitingPrerequisites,
  );
  // Counted by the verb of each row's own action, the same rule as Path to
  // GO › Analyse: a saved row is never "to run" (a saved report once read as
  // never run, 2026-09-18), and a held or failed one is not "to refresh".
  const startableWork = countStartableWork(startableRows);
  const blockedRows = openRows.filter(row => !startableRows.includes(row));
  const inFlightRows = blockedRows.filter(row => !row.awaitingPrerequisites);
  const awaitingRows = blockedRows.filter(row => row.awaitingPrerequisites);
  // Reading order inside a board: startable (numbered) → running → waiting on
  // inputs → current.
  const orderedRows = [...startableRows, ...inFlightRows, ...awaitingRows, ...usedRows];
  const priorityByType = new Map(
    startableRows.map((row, index) => [row.type, index + 1] as const),
  );
  const hasActions = Boolean(onOpenAnalysis || onResolveInputs || onPlanAction);

  // TWO-STAGE boards. The strip and the scenario step read the plan; the
  // boards list the matrix's own rows by stage (registry stage as fallback).
  const boards = planItems && planItems.length > 0 ? withScreeningOutcome(buildStageBoards(planItems), stages) : null;
  const stageOf = (row: AnalysisMatrixRow): DecisionStage | undefined => row.stage ?? reportTypeStage(row.type) ?? undefined;
  const rowsOf = (stage: DecisionStage) => orderedRows.filter(row => (stageOf(row) ?? 1) === stage);
  const boardFor = (stage: DecisionStage, rows: AnalysisMatrixRow[]): StageBoardModel => {
    const fromPlan = boards ? (stage === 1 ? boards.screening : boards.validation) : null;
    const requiredUsed = rows.filter(row => row.completesStage === true).length;
    const requiredCurrent = rows.filter(row => row.calculationStatus === 'current').length;
    const running = rows.some(row => row.lifecycle === 'queued' || row.lifecycle === 'generating');
    const startable = rows.some(row => priorityByType.has(row.type));
    const ownState: StageBoardState = rows.length === 0
      ? 'nothing-required'
      : requiredUsed === rows.length
        ? 'complete'
        : running && !startable ? 'running' : 'open';
    const state: StageBoardState = fromPlan && (fromPlan.state === 'scenario' || fromPlan.state === 'after-screening') && ownState !== 'complete'
      ? fromPlan.state
      : ownState;
    if (journey && fromPlan) return fromPlan;
    return {
      stage,
      label: DECISION_STAGE_LABEL[stage],
      rows: [],
      requiredTotal: rows.length,
      requiredCurrent,
      requiredUsed,
      requiredSaved: rows.filter(row => row.hasRecord).length,
      state,
      ...(fromPlan?.scenario ? { scenario: fromPlan.scenario } : {}),
      ...(stage === 1 && ownState === 'complete' && (fromPlan ? fromPlan.outcome : stages?.screening.complete && stages.screening.outcome)
        ? { outcome: fromPlan?.outcome ?? stages!.screening.outcome, concernTypes: fromPlan?.concernTypes ?? stages!.screening.concernTypes ?? [] }
        : {}),
      ...(stage === 1 && stages?.screening.score != null
        ? {
          score: stages.screening.score,
          scoreCoverage: stages.screening.scoreCoverage ?? 0,
          scoreBasis: ownState === 'complete'
            ? requiredCurrent === rows.length ? 'current' as const : 'saved' as const
            : 'provisional' as const,
        }
        : {}),
    };
  };
  const screeningRows = rowsOf(1);
  const validationRows = rowsOf(2);
  const screeningBoard = boardFor(1, screeningRows);
  const validationBoard = boardFor(2, validationRows);
  const screeningComplete = screeningBoard.state === 'complete' || screeningBoard.state === 'nothing-required';
  const nextType = startableRows[0]?.type ?? null;
  const liveStage: DecisionStage | null = nextType
    ? (stageOf(startableRows[0]!) ?? 1)
    : boards?.next.kind === 'scenario' ? 2 : null;

  // ONE step, not three: rows that wait on nothing but the promoted scenario
  // are listed under the scenario step ("Then · …"), not as rows of their own.
  const foldedTypes = new Set<ReportTypeId>(journey ? [] : (validationBoard.scenario?.dependants ?? []).map(row => row.type));

  const renderRow = (row: AnalysisMatrixRow) => {
    const scenarioLocked = Boolean(journey && stageOf(row) === 2 && validationBoard.scenario?.needed);
    // A historical workspace may be ready on its own while this validation
    // still waits for promotion. Keep those two states distinct in the rack.
    if (scenarioLocked && !row.analysis && row.primaryAction?.kind === 'report-setup') row = {
      ...row, awaitingPrerequisites: true, workflowEvidence: undefined,
      awaitingReason: 'Runs against the promoted scenario',
      awaitingDetail: 'Promote a generation scenario in Generation & Impact before running this validation.',
    };
    const expandable = row.analysis != null && analysisForReport(row.analysis) !== null;
    const expanded = expandable && expandedType === row.type;
    const truthQuestionCount = sharedTruthQuestionCountOf(row.analysis);
    const priorityNumber = scenarioLocked ? undefined : priorityByType.get(row.type);
    const running = row.lifecycle === 'queued' || row.lifecycle === 'generating';
    // A Public Sentiment run waiting for a site-evidence build is queued, not
    // analysing: no countdown, no stall alarm — a waiting notice with Cancel.
    const waitingForEvidence = running && isSentimentEvidenceWaitStage(refreshProgressByType?.[row.type]?.stage);
    const evidenceHold = !running && row.type === 'public-sentiment' ? sentimentEvidenceHold : null;
    const autoRefreshExhausted = autoRebindByType?.[row.type]?.exhausted ?? false;
    // No status badge (owner, 2026-09-30) on a required row the plan laned
    // `available-later` — the dimmed row, its glyph and the awaiting reason
    // already say it, and its raw lifecycle ("Ready to analyse") would be
    // wrong — nor on a quality-held row, whose lifecycle has no badge label.
    // Refresh badges live in the report header; the Analyses page retains
    // the saved-result basis and actions. Running and failed states stay visible.
    const showStatusPill = running || (
      !row.awaitingPrerequisites
      && row.lifecycle !== 'refresh-needed'
      && lifecyclePresentation(row.lifecycle, truthQuestionCount, autoRefreshExhausted).label != null
    );
    // Stale only on optional currency evidence (public sentiment, economic
    // evidence): the plan's live verdict or the pointer's gate marker.
    const newerOptionalEvidence = row.optionalEvidenceOnly === true
      || Boolean(autoRebindByType?.[row.type]?.optionalEvidenceSections?.length);
    const glyph = model.matrixMode === 'required'
      ? (priorityNumber != null
          ? <StageGlyph kind="priority" priority={priorityNumber} data-testid={`findings-analysis-priority-${row.type}`} />
          : row.completesStage === true
            ? <StageGlyph kind="current" />
            // In-flight or waiting on its own inputs: never the green "done"
            // tick an un-run analysis would otherwise inherit.
            : <StageGlyph kind={running ? 'running' : 'later'} data-testid={`findings-analysis-pending-${row.type}`} />)
      : <span className="inline-block h-[18px] w-[18px]" aria-hidden />;
    return (
      <React.Fragment key={row.type}>
        <StageRow
          id={`findings-analysis-${row.type}`}
          glyph={glyph}
          next={row.type === nextType}
          dim={Boolean(row.awaitingPrerequisites)}
          main={(
            <>
              <div className="flex flex-wrap items-center gap-2">
                {expandable ? (
                  <button
                    type="button"
                    onClick={() => setExpandedType(current => (current === row.type ? null : row.type))}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label} analysis detail`}
                    data-testid={`findings-expand-${row.type}`}
                    className="rounded-sm p-0.5 text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <ChevronRight
                      className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')}
                      aria-hidden
                    />
                  </button>
                ) : null}
                <StageRowName>{row.label}</StageRowName>
              </div>
              {priorityNumber != null ? <span className="sr-only">Priority {priorityNumber}. </span> : null}
              {/* Evidence basis + Updated: lifecycle metadata on one line. The raw
                  blocking reason is deliberately NOT rendered here (William,
                  2026-08-11): the state pill names the condition, the action
                  opens the surface that resolves it, the report page carries
                  the full text. */}
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-muted">
                {row.analysis ? (
                  <>
                    <span>{versionLabel(row.analysis)}</span>
                    <span data-testid={`findings-report-sed-currency-${row.type}`}>
                      {screeningSedCurrencyLine(row.analysis, currentSedVersion, planItems, { omitRerunRecommended: true })}
                    </span>
                  </>
                ) : row.workflowEvidence ? (
                  <span
                    className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5"
                    data-testid={`findings-workflow-evidence-${row.type}`}
                    title={row.workflowEvidence.detail}
                  >
                    <span>Generation &amp; Impact workspace</span>
                    <span className={row.workflowEvidence.ready ? 'text-success' : 'text-warning'}>
                      {row.workflowEvidence.title}
                    </span>
                  </span>
                ) : row.awaitingPrerequisites && row.awaitingReason ? (
                  // A row waiting on the promoted scenario says so in four
                  // words; the Stage 2 step above it explains the prerequisite
                  // once. The full reason stays on the tooltip and for
                  // assistive tech.
                  <span data-testid={`findings-awaiting-${row.type}`} title={row.awaitingDetail ?? row.awaitingReason}>
                    {validationBoard.scenario?.needed
                      && row.awaitingReasonCode !== 'generation-scenario-stale'
                      && /generation scenario|promote/i.test(row.awaitingDetail ?? row.awaitingReason)
                      ? (<><span aria-hidden="true">Runs against the promoted scenario</span><span className="sr-only">{row.awaitingDetail ?? row.awaitingReason}</span></>)
                      : row.awaitingReason}
                  </span>
                ) : (
                  <span>No analysis record</span>
                )}
              </p>
            </>
          )}
          state={slotOrNull(
            <div
              className="flex min-w-0 flex-col items-start gap-1"
              title={running ? refreshProgressByType?.[row.type]?.stage : undefined}
            >
              {showStatusPill ? (
                <AnalysisLifecycleStatusPill
                  lifecycle={row.lifecycle}
                  truthQuestionCount={truthQuestionCount}
                  autoRefreshExhausted={autoRefreshExhausted}
                  labelOverride={waitingForEvidence
                    ? 'Waiting for evidence'
                    : running
                    ? (
                        <GenerationCountdown
                          startedAt={refreshProgressByType?.[row.type]?.startedAt}
                          estimatedDurationMs={refreshProgressByType?.[row.type]?.estimatedDurationMs}
                          fallback={refreshProgressByType?.[row.type]?.stage ? 'In progress' : LIFECYCLE_LABEL[row.lifecycle]}
                          testId={`findings-countdown-${row.type}`}
                        />
                      )
                    : undefined}
                />
              ) : null}
              {row.awaitingPrerequisites && row.awaitingReason && row.analysis ? (
                <p className="max-w-[15rem] text-[11px] leading-snug text-text-secondary" data-testid={`findings-awaiting-${row.type}`} title={row.awaitingDetail}>
                  {row.awaitingReason}
                </p>
              ) : null}
              {waitingForEvidence ? (
                <SentimentEvidenceWaitingNotice
                  runId={refreshProgressByType?.[row.type]?.runId}
                  onCancelRun={onCancelRun}
                  className="max-w-[16rem]"
                  data-testid={`findings-evidence-wait-${row.type}`}
                />
              ) : null}
              {evidenceHold ? (
                <SentimentEvidenceHoldNotice
                  hold={evidenceHold}
                  className="max-w-[16rem]"
                  data-testid={`findings-evidence-hold-${row.type}`}
                />
              ) : null}
              {running && !waitingForEvidence && refreshProgressByType?.[row.type] ? (
                <div className="max-w-[15rem]" data-testid={`findings-refresh-progress-${row.type}`}>
                  <div className="h-1 w-full overflow-hidden rounded-sm bg-background-elevated">
                    <div
                      className="h-full rounded-sm bg-primary transition-[width] duration-700"
                      style={{ width: `${Math.max(4, Math.min(100, refreshProgressByType[row.type]?.progress ?? 4))}%` }}
                    />
                  </div>
                  <AnalysisRunHealth
                    rowType={row.type}
                    progress={refreshProgressByType[row.type]}
                    onCancelRun={onCancelRun}
                  />
                </div>
              ) : null}
              {/* `autoRebindExhaustedAt` is sticky; say nothing while a run is in
                  flight — the row already shows what is happening. */}
              {row.lifecycle === 'failed'
                || (
                  autoRebindByType?.[row.type]?.exhausted
                  && !running
                  && !(
                    row.analysis
                    && typeof (row.analysis.sedVersionUsed ?? (row.analysis as { sedVersion?: number }).sedVersion) === 'number'
                    && typeof currentSedVersion === 'number'
                    && (row.analysis.sedVersionUsed ?? (row.analysis as { sedVersion?: number }).sedVersion)! >= currentSedVersion
                  )
                )
                || (autoRebindByType?.[row.type]?.optionalEvidenceSections?.length && !running) ? (
                <RefreshErrorNote
                  error={refreshErrorByType?.[row.type]}
                  truthQuestionCount={truthQuestionCount}
                  autoRebind={autoRebindByType?.[row.type]}
                  informational={row.lifecycle !== 'failed'}
                  data-testid={`findings-refresh-error-${row.type}`}
                />
              ) : null}
            </div>
          )}
          action={hasActions ? (
            // OPEN and the plan action are INDEPENDENT: a stale analysis is
            // still the thing the user reads before deciding to re-run, so both
            // render. Retry lives on the report page only.
            <div className="flex items-center justify-end gap-2">
              {running ? (
                <>
                  {row.analysis && onOpenAnalysis ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => onOpenAnalysis(row.analysis!.id)} aria-label={`Open ${row.label} analysis`} data-testid={`findings-open-${row.type}`}>
                      Open
                      <ArrowUpRight aria-hidden />
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" variant="ghost" disabled>
                    <Clock3 aria-hidden />
                    {/artifact|render|pdf|docx|pptx|export/i.test(refreshProgressByType?.[row.type]?.stage ?? "")
                      ? "Preparing exports"
                      : "Updating"}
                  </Button>
                </>
              ) : (
                <>
                  {row.analysis && onOpenAnalysis ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => onOpenAnalysis(row.analysis!.id)} aria-label={`Open ${row.label} analysis`} data-testid={`findings-open-${row.type}`}>
                      Open
                      <ArrowUpRight aria-hidden />
                    </Button>
                  ) : null}
                  {row.analysis && siteId ? <ScreeningSummaryActions report={row.analysis} siteId={siteId} /> : null}
                  {/* Report-setup plan actions on failed/refresh/held rows ARE
                      retries — suppressed (reruns live on the report page).
                      Genuinely different next steps still render. */}
                  {!row.analysis && row.primaryAction && onPlanAction
                    && !(
                      (
                        row.lifecycle === 'failed'
                        || row.lifecycle === 'refresh-needed'
                        || row.lifecycle === 'quality-held'
                      )
                      && row.primaryAction.kind === 'report-setup'
                    ) ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={!journey && row.type === nextType ? 'mission-primary' : 'outline'}
                      onClick={() => onPlanAction(journey && stageOf(row) === 2 && validationBoard.scenario?.needed && row.primaryAction?.kind === 'report-setup' ? { kind: 'generation-impact', label: 'Unlock in Generation & Impact', focus: 'promotion' } : row.primaryAction!, row.type)}
                      aria-label={`${journey && stageOf(row) === 2 && validationBoard.scenario?.needed && row.primaryAction.kind === 'report-setup' ? 'Unlock in Generation & Impact' : row.primaryAction.label} for ${row.label}`}
                      data-testid={`findings-plan-action-${row.type}`}
                      data-action-kind={row.primaryAction.kind}
                    >
                      {journey && stageOf(row) === 2 && validationBoard.scenario?.needed && row.primaryAction.kind === 'report-setup' ? 'Unlock in Generation & Impact' : row.primaryAction.label}
                      <ArrowUpRight aria-hidden />
                    </Button>
                  ) : !row.analysis && onResolveInputs ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => onResolveInputs(row.type)} aria-label={`Resolve inputs for ${row.label}`} data-testid={`findings-resolve-inputs-${row.type}`}>
                      Resolve inputs
                      <ArrowUpRight aria-hidden />
                    </Button>
                  ) : null}
                  {!row.analysis
                    && !onResolveInputs
                    && !(row.primaryAction && onPlanAction) ? (
                      <span className="text-xs text-text-muted">Not started</span>
                    ) : null}
                  {row.analysis && !onOpenAnalysis ? (
                    <span className="text-xs text-text-muted">Available</span>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          data-testid={`findings-analysis-row-${row.type}`}
          data-lifecycle={row.lifecycle}
        />
        {expanded && row.analysis ? (
          <li className="border-b border-border-subtle bg-background/45 px-4 py-3 last:border-b-0" data-testid={`findings-expansion-${row.type}`}>
            <DecisionAnalysisExpansion report={row.analysis} />
          </li>
        ) : null}
      </React.Fragment>
    );
  };

  const boardOf = (board: StageBoardModel, rows: AnalysisMatrixRow[]) => {
    const awaiting = rows.some(row => row.awaitingPrerequisites);
    const shown = rows.filter(row => !foldedTypes.has(row.type));
    const subline = awaiting
      ? 'available when inputs are ready'
      : board.stage === 1 && (board.outcome || board.score != null)
        ? [board.outcome ? screeningOutcomeLabel(board.outcome, board.concernTypes) : null, board.score != null ? screeningScoreShort(board.score, board.scoreCoverage, board.scoreBasis) : null].filter(Boolean).join(' · ')
        : undefined;
    return (
      <StageBoard
        className="findings-stage-board"
        board={board}
        live={liveStage === board.stage}
        subline={subline}
        headerTestId={awaiting ? `findings-stage-group-${board.stage}` : undefined}
        data-testid={`findings-board-${board.stage}`}
      >
        {board.stage === 2 && board.scenario && !journey ? (
          <ScenarioStep
            scenario={board.scenario}
            screeningComplete={screeningComplete}
            onAction={onPlanAction ? (action) => onPlanAction(action) : undefined}
            then={board.scenario.dependants.map(row => analysisLabel(row.type))}
            data-testid="findings-scenario-step"
          />
        ) : null}
        <ol className="list-none p-0" aria-label={`${board.label} analyses`}>
          {shown.map(renderRow)}
          {shown.length === 0 ? (
            <li className="px-3.5 py-3 text-[12px] text-text-muted">Nothing required at this stage.</li>
          ) : null}
        </ol>
      </StageBoard>
    );
  };

  return (
  <section
    className="rounded-lg border border-border bg-card shadow-card"
    aria-labelledby="findings-analysis-matrix-title"
    data-testid="findings-analysis-matrix"
  >
    {showDecisionPath && journey && boards ? <div className="p-4 md:p-5"><DecisionPath compact boards={boards} step={buildSiteNextStep(journey.nextAction, boards)} decisionLabel={decision?.label} testPrefix="findings" loading={journey.loading} onRun={() => onRunNextAction ? onRunNextAction(journey.nextAction) : onPlanAction?.(journey.nextAction.pathAction)} /></div> : null}
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border-subtle px-4 py-3.5 md:px-5">
      <div>
        <p className="type-meta uppercase tracking-[0.14em] text-primary">
          Decision analyses
        </p>
        <h2
          id="findings-analysis-matrix-title"
          className="mt-1 type-section text-text-primary"
        >
          {model.matrixMode === 'required'
            ? 'What supports the decision now'
            : 'Available analyses'}
        </h2>
        {model.matrixMode !== 'required' ? (
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            Decision requirements are not connected; these records are shown for review only.
          </p>
        ) : null}
      </div>
      <span className="text-sm font-semibold tabular-nums text-text-primary" data-testid="findings-analysis-count">
        {model.matrixMode === 'required'
          ? startableRows.length > 0
            ? startableWorkLabel(startableWork)
            // Nothing startable is NOT the same as nothing outstanding.
            : blockedRows.some(row => row.awaitingPrerequisites)
              ? 'Waiting on inputs'
              : blockedRows.length > 0
                ? 'Updating'
                : usesSavedBasis ? 'Using saved decision basis' : 'Decision basis current'
          : `${model.matrixRows.length} ${model.matrixRows.length === 1 ? 'analysis' : 'analyses'}`}
      </span>
    </div>

    {model.matrixRows.length > 0 ? (
      model.matrixMode === 'required' ? (
        <div className="space-y-4 p-4 md:p-5">
          {boards && !journey ? <StageStrip model={boards} decision={decision} data-testid="findings-stage-strip" /> : null}
          <div className="grid gap-5" data-testid="findings-boards">
            {boardOf(screeningBoard, screeningRows)}
            <div className={journey && validationBoard.scenario?.needed ? 'instrument-rack-locked' : undefined}>
              {journey && validationBoard.scenario?.needed ? <p className="px-4 py-3 type-meta text-text-muted" data-testid="findings-scenario-step">Runs against a promoted generation scenario. Prepare the other inputs while it is selected.</p> : null}
              {boardOf(validationBoard, validationRows)}
            </div>
          </div>
        </div>
      ) : (
        <ol className="@container list-none p-0" aria-label="Available analyses">
          {orderedRows.map(renderRow)}
        </ol>
      )
    ) : (
      <div className="px-4 py-8 text-center md:px-5">
        <CircleDashed className="mx-auto h-5 w-5 text-text-muted" aria-hidden />
        <p className="mt-2 text-sm font-medium text-text-secondary">
          No analyses are in scope yet
        </p>
        <p className="mt-1 text-xs text-text-muted">
          Findings will appear here as analysis records become available.
        </p>
      </div>
    )}
  </section>
  );
};

/**
 * Editorial citation run (2026-08-17) — sources are CITATIONS, not buttons.
 * The old bordered pills out-weighed the finding they annotated (5-7 chips
 * per row); per the EditorialBadge house rule ("mono, uppercase, status ink —
 * NOT a filled chip") they now render as an unboxed small-caps run separated
 * by interpuncts, collapsing past 3 to "FIRST + N SOURCES" with the full run
 * on the title tooltip and aria-label.
 */
const SourceTags: React.FC<{ sources: readonly AnalysisSource[] }> = ({ sources }) => {
  if (sources.length === 0) return null;
  const collapsed = sources.length > 3;
  const shown = collapsed ? sources.slice(0, 1) : sources;
  const full = sources.map(s => `${s.label} ${s.versionLabel}`).join(' · ');
  return (
    <span
      className="text-[10px] font-medium uppercase leading-relaxed tracking-[0.08em] text-text-muted"
      aria-label={`Analysis sources: ${full}`}
      title={collapsed ? full : undefined}
    >
      {shown.map((source, i) => (
        <span key={source.id} className="whitespace-nowrap">
          {i > 0 ? <span aria-hidden> · </span> : null}
          {source.label} {source.versionLabel}
        </span>
      ))}
      {collapsed ? (
        <span className="whitespace-nowrap"> + {sources.length - 1} sources</span>
      ) : null}
    </span>
  );
};


/**
 * The two things a merged row must never be able to swallow: a disagreement
 * between its members, and the members themselves.
 *
 * Shared because a lane renders through THREE paths — the Metric|Value|Score
 * table, the condition bars, and the narrative list — and a guarantee that
 * holds on only one of them is not a guarantee.
 *
 * The member list is suppressed when every analysis stamped identical text
 * (`membersDiffer === false`): listing seven copies of one sentence tells the
 * reader nothing the source count has not already said.
 */
const ClusterDisclosure: React.FC<{
  cluster: DossierEvidenceItem;
  laneId: string;
}> = ({ cluster, laneId }) => {
  const showMembers = cluster.members.length > 1 && cluster.membersDiffer;
  if (!cluster.disagreement && !showMembers) return null;
  return (
    <>
      {cluster.disagreement ? (
        <p
          className="text-[11px] font-medium leading-relaxed text-danger"
          data-testid={`findings-disagreement-${laneId}`}
        >
          <TriangleAlert className="mr-1 inline h-3 w-3 align-[-1px]" aria-hidden />
          {cluster.disagreement.summary}
        </p>
      ) : null}
      {showMembers ? (
        <details data-testid={`findings-members-${laneId}`}>
          <summary className="cursor-pointer list-none text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted hover:text-text-primary">
            <ChevronRight className="mr-1 inline h-3 w-3 align-[-1px]" aria-hidden />
            {cluster.members.length} contributing statements
          </summary>
          <ul className="mt-1 space-y-1 border-l border-border-subtle pl-3">
            {cluster.members.map(member => (
              <li
                key={`${member.source.id}:${member.title}`}
                className="max-w-[72ch] text-xs leading-relaxed text-text-secondary"
              >
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                  {member.source.label} {member.source.versionLabel}
                </span>
                <span aria-hidden> · </span>
                {member.title}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
};

/**
 * Label of the analysis whose verbatim wording won a merged row's headline.
 * Merged rows never carry re-voiced prose (see `findingClusters` R1), so the
 * reader is told whose sentence they are reading.
 */
function headlineSourceLabel(cluster: DossierEvidenceItem): string {
  const source = cluster.sources.find(entry => entry.id === cluster.headlineSourceId);
  return source ? `${source.label} ${source.versionLabel}` : 'one analysis';
}

/* Editorial lane (2026-08-17) — "the dossier is the platform's reading
   room": each lane composes as a DOCUMENT SECTION, not a dashboard card.
   The card box, icon chip, and StatusPill are replaced by the reports'
   apparatus: a strong ink rule with a tone-coloured thumb-index segment,
   a numbered small-caps heading with a typographic count, and an italic
   standfirst. Tone survives in the thumb index and the meters, not in
   badge chrome. */
const EvidenceLane: React.FC<{
  id: string;
  /** 1-based position in the lane order — renders as the 01-05 folio number. */
  ordinal: number;
  title: string;
  description: string;
  items: readonly DossierEvidenceItem[];
  tone: 'positive' | 'risk' | 'gap' | 'condition' | 'action';
  /** Screening-vs-analysis disagreements, matched onto their screening row. */
  contradictions?: readonly CrossBandContradiction[];
}> = ({
  id,
  ordinal,
  title,
  description,
  items,
  tone,
  contradictions,
}) => {
  const toneSeg = {
    positive: 'bg-success',
    risk: 'bg-danger',
    gap: 'bg-warning',
    condition: 'bg-conditional-high',
    action: 'bg-primary',
  }[tone];

  return (
    <section
      aria-labelledby={`findings-lane-${id}-title`}
      data-testid={`findings-lane-${id}`}
    >
      <div
        aria-hidden
        className="flex h-[1.5px] w-full origin-left animate-[findings-rule-draw_0.5s_ease-out] motion-reduce:animate-none"
      >
        <span className={cn('w-10 shrink-0', toneSeg)} />
        <span className="flex-1 bg-text-primary" />
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 pb-3 pt-2.5">
        <h2
          id={`findings-lane-${id}-title`}
          className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-primary"
        >
          <span className="tabular-nums text-text-muted">{String(ordinal).padStart(2, '0')}</span>
          <span className="text-text-muted" aria-hidden> · </span>
          {title}
          <span className="text-text-muted" aria-hidden> · </span>
          <span className="tabular-nums">{items.length}</span>
        </h2>
        <p className="max-w-[65ch] text-xs italic leading-relaxed text-text-muted">
          {description}
        </p>
      </div>
      {items.length > 0 ? (() => {
        // B+ (2026-08-03): recover the structure trapped in the prose. Items
        // that parse become a Metric | Value | Score table; conditions with
        // inline scores become bars; everything else stays a narrative card.
        const rows = items.map(item => ({
          item,
          fact: parseLaneFact(item.headline, item.detail),
          cond: tone === 'condition' ? parseConditionScore(item.headline, item.detail) : null,
        }));
        // Screening chips are deterministic checks stamped into every analysis,
        // not findings the analyses reached. They stay in the facts table (that
        // is what they are) but sort BELOW the analysis-derived metrics, and
        // their source cell reads as repetition rather than corroboration.
        const facts = rows.filter(row => row.fact !== null)
          .sort((left, right) => (
            Number(left.item.band === 'screening') - Number(right.item.band === 'screening')
          ));
        const bars = rows.filter(row => row.fact === null && row.cond !== null);
        const narrative = rows.filter(row => row.fact === null && row.cond === null)
          .map(row => row.item);
        // Editorial meter — numeral LEADS in tabular mono so the column
        // aligns; the bar is a squared 3px segment on a hairline track
        // (flush ends, no pill rounding), filled in the LANE's tone rather
        // than a per-score traffic light: the reports reserve red/amber ink
        // for named risks, and a threshold repaint read as a heatmap.
        const meter = (score: number) => (
          <span className="inline-flex items-center gap-2">
            <span className="w-6 text-right tabular-nums text-text-secondary">{score}</span>
            <span className="h-[3px] w-16 overflow-hidden bg-background-elevated">
              <span className={cn('block h-full', toneSeg)} style={{ width: `${score}%` }} />
            </span>
          </span>
        );
        const stagger = (i: number): React.CSSProperties => ({
          animationDelay: `${Math.min(i, 8) * 40}ms`,
        });
        const rowEntrance =
          'animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300 motion-reduce:animate-none';
        return (
        <div>
        {facts.some(row => row.fact?.fromBoilerplate) ? (
          <div className="border-b border-border-subtle py-2">
            <p className="text-[11px] italic text-text-muted">{BOILERPLATE_CAPTION}</p>
            {/* A screening chip and the analyses can read the SAME underlying
                number in opposite directions (live: WRI Aqueduct 1.53/5 is
                "Moderate - mitigation required" to the screening pass and
                "Low - favorable" to the water analysis). Neither is silently
                preferred; the page says they disagree. */}
            {(contradictions ?? [])
              .filter(entry => facts.some(row => row.item.subjectId === entry.subjectId))
              .map(entry => (
                <p
                  key={entry.subjectId}
                  data-testid={`findings-screening-contradiction-${entry.subjectId}`}
                  className="mt-1 text-[11px] font-medium text-danger"
                >
                  {entry.summary}
                </p>
              ))}
          </div>
        ) : null}
        {facts.length > 0 ? (
          /* Editorial table anatomy (matches EditorialDataTable in the report
             reader, mapped to app tokens): mono small-caps header row over a
             single strong ink underline, hairline-separated rows, strong-ink
             name column, tabular numerics, and a closing ink foot rule. The
             divide-y tbody puts no top border on the first row, so the header
             underline stays a SINGLE rule. Flush-left now that the card box
             is gone — the first column aligns with the section heading. */
          <table className="w-full border-b-[1.5px] border-text-primary text-xs" data-testid={`findings-facts-${id}`}>
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-text-muted">
                <th className="border-b-[1.5px] border-text-primary py-2 pr-4 font-semibold">Metric</th>
                <th className="border-b-[1.5px] border-text-primary py-2 pr-4 font-semibold">Value</th>
                <th className="border-b-[1.5px] border-text-primary py-2 pr-4 font-semibold">Score</th>
                <th className="border-b-[1.5px] border-text-primary py-2 pl-4 text-right font-semibold">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {facts.map((row, i) => (
                <React.Fragment key={row.item.key}>
                <tr data-testid={`findings-item-${id}`} className={cn(rowEntrance, 'transition-colors hover:bg-background-elevated/40')} style={stagger(i)}>
                  <td className="py-2.5 pr-4 font-semibold text-text-primary">{row.fact!.metric}</td>
                  <td className="py-2.5 pr-4 tabular-nums text-text-secondary">{row.fact!.value}</td>
                  <td className="py-2.5 pr-4">
                    {row.fact!.score != null
                      ? meter(row.fact!.score)
                      : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="py-2.5 pl-4 text-right">
                    {row.item.band === 'screening'
                      ? (
                        <span
                          className="text-[10px] font-medium uppercase leading-relaxed tracking-[0.08em] text-text-muted"
                          title={row.item.sources.map(x => `${x.label} ${x.versionLabel}`).join(' \u00b7 ')}
                        >
                          Screening {'\u00b7'} applied to {row.item.sources.length} analyses
                        </span>
                      )
                      : <SourceTags sources={row.item.sources} />}
                  </td>
                </tr>
                {/* A screening chip's own value ("12-24 months", "Zone A") is
                    its only varying content, so a divergent one would otherwise
                    be represented by whichever analysis happened to win. */}
                {row.item.disagreement || (row.item.members.length > 1 && row.item.membersDiffer) ? (
                  <tr className="hover:bg-transparent">
                    <td colSpan={4} className="whitespace-normal break-words pb-2.5">
                      <ClusterDisclosure cluster={row.item} laneId={id} />
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : null}
        {bars.length > 0 ? (
          <ul className="divide-y divide-border-subtle border-b border-border-subtle" data-testid={`findings-bars-${id}`}>
            {bars.map((row, i) => (
              <li key={row.item.key} className={cn('py-2', rowEntrance, 'transition-colors hover:bg-background-elevated/40')} style={stagger(i)} data-testid={`findings-item-${id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="min-w-0 max-w-[72ch] text-xs font-semibold text-text-primary">{row.cond!.label}</span>
                  <span className="flex shrink-0 items-center gap-4 text-xs">
                    {meter(row.cond!.score)}
                    <SourceTags sources={row.item.sources} />
                  </span>
                </div>
                <ClusterDisclosure cluster={row.item} laneId={id} />
              </li>
            ))}
          </ul>
        ) : null}
        <ul className="divide-y divide-border-subtle">
          {/* Editorial item (2026-08-17): left accent stripes were removed
              REPORT-WIDE in the reader (William QA 2026-06-28), so the summary
              drops its coloured stripe to match. Severity renders as
              EditorialBadge's DEFAULT variant — the bare small-caps ink tag,
              no border (outline is reserved for risk-register cells). The
              item body holds a 72ch reading measure with sources set as
              MARGINALIA in a right-hand citation column, like the reports'
              citation margin; below md the margin folds under the text. */}
          {narrative.map((item, i) => (
            <li
              key={item.key}
              className={cn(
                'grid gap-x-8 gap-y-1 py-2.5 md:grid-cols-[minmax(0,72ch)_minmax(8rem,1fr)]',
                rowEntrance,
                'transition-colors hover:bg-background-elevated/40',
              )}
              style={stagger(i)}
              data-testid={`findings-item-${id}`}
              data-cluster-size={item.members.length}
            >
              <p className="min-w-0 text-sm leading-snug">
                <span className="font-semibold text-text-primary">
                  {isWithheldNarrativeNotice(item.headline)
                    ? <WithheldNarrativeNotice variant="inline" />
                    : item.headline}
                </span>
                {item.severity ? (
                  <span
                    className={cn(
                      'ml-2 align-middle text-[10px] font-semibold uppercase tracking-[0.12em]',
                      /negative|high|critical/i.test(item.severity)
                        ? 'text-danger'
                        : /positive|low/i.test(item.severity)
                          ? 'text-success'
                          : /medium|moderate|conditional/i.test(item.severity)
                            ? 'text-warning'
                            : 'text-text-muted',
                    )}
                  >
                    {item.severity}
                  </span>
                ) : null}
                {item.detail ? (
                  <span className="text-xs leading-relaxed text-text-secondary">
                    {' — '}
                    {isWithheldNarrativeNotice(item.detail)
                      ? <WithheldNarrativeNotice variant="inline" />
                      : item.detail}
                  </span>
                ) : null}
                {/* The merged row shows ONE analysis's wording. Naming that
                    analysis inline stops the headline reading as a house
                    summary of all of them — nothing here is re-voiced. */}
                {item.members.length > 1 && item.membersDiffer ? (
                  <span className="text-[11px] italic text-text-muted">
                    {' '}(as stated by {headlineSourceLabel(item)})
                  </span>
                ) : null}
              </p>
              <span className="md:justify-self-end md:text-right">
                <SourceTags sources={item.sources} />
              </span>
              <div className="md:col-span-2 md:mt-0.5">
                <ClusterDisclosure cluster={item} laneId={id} />
              </div>
            </li>
          ))}
        </ul>
        </div>
        );
      })() : (
        // Matches the reader's insufficient-data note: flush-left italic
        // muted prose, no box, no accent (William QA 2026-06-28).
        <p className="py-4 text-xs italic text-text-muted">
          No {title.toLocaleLowerCase()} are recorded in the selected decision basis.
        </p>
      )}
    </section>
  );
};

function historyStateVariant(state: string): StatusPillVariant {
  if (
    state === 'ready'
    || state === 'current'
    || state === 'approved'
  ) return 'success';
  if (
    state === 'failed'
    || state === 'rejected'
  ) return 'danger';
  // Held/degraded = an analysis EXISTS but awaits revision — amber, matching
  // the matrix's held presentation, not the red of a run that produced nothing.
  if (
    state === 'held'
    || state === 'degraded'
  ) return 'warn';
  if (
    state === 'queued'
    || state === 'refreshing'
    || state === 'staged'
    || state === 'submitted'
  ) return 'custom';
  return 'neutral';
}

const DecisionSignalSummary: React.FC<{
  model: FindingsDossierModel;
}> = ({ model }) => {
  const columns: ReadonlyArray<{
    id: string;
    label: string;
    empty: string;
    tone: string;
    items: readonly DossierEvidenceItem[];
  }> = [
    { id: 'finding', label: 'Key findings', empty: 'No key finding is established yet.', tone: 'bg-primary', items: model.findings },
    { id: 'threat', label: 'Could stop progress', empty: 'No material threat is established yet.', tone: 'bg-danger', items: model.risks },
    { id: 'missing', label: 'Still needs proof', empty: 'No evidence gap is recorded.', tone: 'bg-warning', items: model.gaps },
  ];
  return (
    <section className="mt-5" aria-labelledby="decision-signals-title" data-testid="findings-decision-signals">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-[1.5px] border-text-primary pb-2">
        <div>
          <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.13em] text-primary">Decision signals</p>
          <h2 id="decision-signals-title" className="mt-1 font-sans text-xl font-semibold tracking-[-0.02em] text-text-primary">
            The short list that matters now
          </h2>
        </div>
        <p className="font-mono text-[9px] text-text-muted">
          Top 3 per signal · {model.conditions.length} decision condition{model.conditions.length === 1 ? '' : 's'} retained
        </p>
      </div>
      <div className="grid border-b border-border-subtle md:grid-cols-3">
        {columns.map((column, columnIndex) => (
          <section
            key={column.id}
            className={cn('min-w-0 py-4 md:px-4', columnIndex > 0 && 'border-t border-border-subtle md:border-l md:border-t-0', columnIndex === 0 && 'md:pl-0', columnIndex === columns.length - 1 && 'md:pr-0')}
            aria-label={column.label}
          >
            <div className="flex items-center gap-2">
              <span className={cn('h-1.5 w-1.5', column.tone)} aria-hidden />
              <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-text-primary">{column.label}</h3>
              <span
                className="font-mono text-[9px] tabular-nums text-text-muted"
                title={column.items.length > 3 ? `Showing top 3 of ${column.items.length} total items` : `${column.items.length} total items`}
              >
                {column.items.length}
              </span>
            </div>
            {column.items.length > 0 ? (
              <ol className="mt-3 divide-y divide-border-subtle">
                {column.items.slice(0, 3).map((item) => (
                  <li key={item.key} className="py-2.5 first:pt-0">
                    <p className="line-clamp-3 text-sm font-semibold leading-snug text-text-primary">{item.headline}</p>
                    {column.id === 'missing' && item.detail && item.detail !== item.headline ? (
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-text-secondary">
                        {item.detail}
                      </p>
                    ) : null}
                    <p className="mt-1 font-mono text-[9px] text-text-muted">
                      {item.sources.length} source{item.sources.length === 1 ? '' : 's'}
                    </p>
                  </li>
                ))}
              </ol>
            ) : <p className="mt-3 text-xs leading-relaxed text-text-muted">{column.empty}</p>}
          </section>
        ))}
      </div>
    </section>
  );
};

const HistoryPanel: React.FC<{
  model: FindingsDossierModel;
  decisionRecords: readonly PathToGoDecisionRecord[];
}> = ({ model, decisionRecords }) => (
  <section
    className="rounded-lg border border-border bg-card shadow-card"
    aria-labelledby="findings-history-title"
    data-testid="findings-history"
  >
    <div className="flex items-start gap-3 border-b border-border-subtle px-4 py-3.5 md:px-5">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-background text-text-secondary"
        aria-hidden
      >
        <History className="h-4 w-4" />
      </span>
      <div>
        <p className="type-meta uppercase tracking-[0.14em] text-primary">
          Accountability
        </p>
        <h2 id="findings-history-title" className="mt-0.5 type-section text-text-primary">
          Decision, analysis, and export history
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-text-muted">
          Semantic revisions and human decisions remain separate from optional
          printable artifacts.
        </p>
      </div>
    </div>

    <div className="divide-y divide-border-subtle">
      <div className="min-w-0 p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-text-primary">Decision records</h3>
          <StatusPill variant="neutral">{decisionRecords.length}</StatusPill>
        </div>
        {decisionRecords.length > 0 ? (
          <ol className="mt-3 space-y-2" data-testid="findings-decision-history">
            {decisionRecords
              .slice()
              .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
              .map(record => (
                <li
                  key={record.id}
                  className="rounded-lg border border-border-subtle bg-background/45 px-3 py-2.5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-text-primary">
                      v{record.version} · {record.decision.replace('_', ' ')}
                    </p>
                    <StatusPill variant={historyStateVariant(record.approvalStatus)}>
                      {record.approvalStatus}
                    </StatusPill>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">
                    {readableDate(record.createdAt)} · {record.decisionOwner}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-text-secondary">
                    {record.rationale}
                  </p>
                </li>
              ))}
          </ol>
        ) : (
          <p className="mt-3 text-xs text-text-muted">
            No human decision has been recorded.
          </p>
        )}
      </div>

      <div className="min-w-0 p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-text-primary">Analysis revisions</h3>
          <StatusPill variant="neutral">{model.revisionHistory.length}</StatusPill>
        </div>
        {model.revisionHistory.length > 0 ? (
          <ol className="mt-3 space-y-2" data-testid="findings-revision-history">
            {model.revisionHistory.map(row => (
              <li
                key={row.key}
                className="rounded-lg border border-border-subtle bg-background/45 px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-text-primary">{row.analysis}</p>
                  {row.state && (
                    <StatusPill variant={historyStateVariant(row.state)}>
                      {row.state}
                    </StatusPill>
                  )}
                </div>
                <p className="mt-1 font-mono text-[10px] text-text-muted">
                  {row.revision}
                </p>
                <p className="mt-1 text-xs text-text-muted">{readableDate(row.createdAt)}</p>
                {row.cause && (
                  <p className="mt-1.5 text-xs text-text-secondary">{row.cause}</p>
                )}
                {row.changes.length > 0 && (
                  <ul
                    className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1"
                    data-testid="revision-changes"
                  >
                    {row.changes.map(change => (
                      <li key={change} className="text-[11px] text-text-muted">
                        {change}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-3 text-xs text-text-muted">
            No immutable semantic revision is loaded.
          </p>
        )}
      </div>

      <div className="min-w-0 p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-text-primary">Optional exports</h3>
          <StatusPill variant="neutral">{model.exportHistory.length}</StatusPill>
        </div>
        {model.exportHistory.length > 0 ? (
          <ol className="mt-3 space-y-2" data-testid="findings-export-history">
            {model.exportHistory.map(row => (
              <li
                key={row.key}
                className="rounded-lg border border-border-subtle bg-background/45 px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-text-primary">
                    {row.analysis} · {row.format}
                  </p>
                  <StatusPill variant={historyStateVariant(row.state)}>
                    {row.state}
                  </StatusPill>
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  {readableDate(row.createdAt)} · {row.detail}
                </p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-3 text-xs text-text-muted">
            No printable artifact has been created. This does not affect
            analysis readiness.
          </p>
        )}
      </div>
    </div>
  </section>
);

/**
 * Continuous, responsive decision dossier. It renders existing report-shaped
 * records as compatibility analyses and intentionally performs no navigation,
 * persistence, generation, or export work itself.
 */
/**
 * Memoised: the host page re-renders on live run progress, local UI state and
 * tab switches that leave every dossier prop untouched. The host passes
 * stable handler identities for exactly this reason; the two model builds
 * below are already memoised on their data inputs.
 */
export const FindingsDossier: React.FC<FindingsDossierProps> = React.memo(function FindingsDossierImpl({
  site,
  surface = 'combined',
  analyses: rawAnalyses,
  analysesState = 'settled',
  callReadyEnabled = false,
  economicContributionEnabled = false,
  onRunEconomicScreen,
  economicResearch,
  economicWorkforceProfile,
  derivedVerdict,
  pathToGo,
  journey,
  composite,
  onRunNextAction,
  requiredAnalysisTypes,
  sed,
  generationImpactEnabled,
  focusAnalysisId,
  onFocusAnalysisHandled,
  onOpenAnalysis,
  onOpenAnalyses,
  onOpenPhase,
  briefDecisionStage,
  decisionReviewStage,
  onDecisionReviewStageChange,
  onOpenBriefRoute,
  phaseDestinationLabels,
  onLaunchAnalysis,
  isDatacrateAdmin,
  onResolveInputs,
  onPlanAction,
  refreshErrorByType,
  autoRebindByType,
  refreshProgressByType,
  onCancelRun,
  inflightByType,
  decisionRecords = [],
  briefScreening,
  onReviewScreeningDecision,
  className,
}: FindingsDossierProps) {
  const isDeferredLoadingSurface =
    surface !== 'combined'
    && analysesState === 'loading'
    && (surface === 'decision' || surface === 'analyses');
  const effectiveRawAnalyses = useMemo(
    () => (isDeferredLoadingSurface ? [] : rawAnalyses),
    [isDeferredLoadingSurface, rawAnalyses],
  );
  // Every surface below (lanes, call-ready summary, optional analyses) reads
  // the PRESENTED text: withheld narrative reads "not assessed" and a stored
  // legacy quality-gate sentinel never renders. Same array when clean.
  const analyses = useMemo(() => presentReportsText(effectiveRawAnalyses), [effectiveRawAnalyses]);
  const model = useMemo(
    () => buildFindingsDossierModel({
      site,
      analyses,
      pathToGo,
      requiredAnalysisTypes,
      sed,
      generationImpactEnabled,
      journey,
    }),
    [site, analyses, pathToGo, requiredAnalysisTypes, sed, generationImpactEnabled, journey],
  );
  // The selected reading view is separate from the workflow stage. Saved
  // Stage 1 outcomes remain reachable after the workflow advances to Stage 2.
  const reviewScope = JSON.stringify([site.id, site._ownerUid]);
  const [reviewStageOverride, setReviewStageOverride] = useState<{ scope: string; stage: 1 | 2 } | null>(null);
  const selectedDecisionStage = decisionReviewStage
    ?? (reviewStageOverride?.scope === reviewScope ? reviewStageOverride.stage : undefined)
    ?? briefDecisionStage?.currentStage
    ?? 2;
  const selectDecisionStage = (stage: 1 | 2) => {
    setReviewStageOverride({ scope: reviewScope, stage });
    onDecisionReviewStageChange?.(stage);
  };
  const screeningCockpit = selectedDecisionStage === 1;
  const stagePlan = useMemo(() => pathToGo?.reportPlan.filter(item => (
    reportTypeStage(item.type) === selectedDecisionStage && item.role !== 'optional'
  )), [pathToGo, selectedDecisionStage]);
  const stageReports = useMemo(() => selectDecisionStageReports(
    analyses, selectedDecisionStage, stagePlan, site.id,
  ), [analyses, selectedDecisionStage, stagePlan, site.id]);
  const stageModel = useMemo(() => buildFindingsDossierModel({
    site,
    analyses: stageReports,
    pathToGo: pathToGo ? {
      ...pathToGo,
      reportPlan: stagePlan ?? [],
      requiredReports: pathToGo.requiredReports.filter(type => reportTypeStage(type) === selectedDecisionStage),
    } : null,
    requiredAnalysisTypes: requiredAnalysisTypes?.filter(type => reportTypeStage(type) === selectedDecisionStage),
    sed,
    generationImpactEnabled,
  }), [site, stageReports, pathToGo, stagePlan, requiredAnalysisTypes, selectedDecisionStage, sed, generationImpactEnabled]);
  const decisionModel = callReadyEnabled || briefDecisionStage || decisionReviewStage != null ? stageModel : model;
  const awaitingStageReports = decisionModel.latestAnalyses.length === 0;
  // Live owner/site metadata, not a global SED snapshot or another report's version.
  const currentSedVersion = (site.sedStatus === 'complete' || site.sedStatus === 'partial') && site.sedId ? site.sedVersion : null;
  const screeningRowReport = decisionModel.matrixRows.find(row => row.type === 'feasibility')?.analysis;

  const scopedBriefScreening = briefScreening?.scope === JSON.stringify([site.id, site._ownerUid ?? null])
    ? briefScreening : null;
  const callReadyModel = useMemo(
    () => callReadyEnabled
      ? buildCallReadyFindingsModel(site, analyses, {
          economicContributionEnabled,
          recommendationFirst: true,
          decisionStage: briefDecisionStage,
          screeningRecommendationBasis: briefScreening
            ? scopedBriefScreening?.sourceStatus === 'settled' ? scopedBriefScreening.currentBasis : null
            : undefined,
          analysesState,
          sed: sed
            && site.sedId === sed.id
            && site.sedVersion === sed.version
            && site.sedStatus !== 'bootstrapping'
            && site.sedStatus !== 'failed'
            ? sed
            : null,
        })
      : null,
    [analyses, analysesState, briefDecisionStage, briefScreening, scopedBriefScreening, callReadyEnabled, economicContributionEnabled, sed, site],
  );
  const effectiveVerdict = pathToGo?.verdict ?? derivedVerdict;
  const screeningStage = pathToGo?.decisionRollup?.stages?.screening;
  const readiness = screeningCockpit ? {
    label: 'Stage 1 · Early screening',
    detail: screeningStage ? stageLine(screeningStage) : 'Review the available screening results and unresolved source evidence.',
  } : callReadyEnabled && awaitingStageReports ? {
    label: 'Stage 2 · Mature validation',
    detail: 'Awaiting relevant generated validation reports. Saved screening remains available in Stage 1.',
  } : readinessPresentation(effectiveVerdict, pathToGo);
  // Count only rows the matrix BELOW actually renders — a count with no
  // visible row and no route breaks the "counts + Open report" contract
  // (final pre-deploy review, finding #2).
  const auditRecommendedCount = decisionModel.matrixRows
    .filter(row => row.auditRecommended).length;
  // ROUND 9 — how many of those flagged rows actually ITEMISE anything. The
  // line below asserted "the verdict includes their findings", which is false
  // for a FLAGGED-FLOOR analysis: `kind: 'flagged'` means every stamp was
  // superseded, the disclosure's count is the never-disclose-zero floor, and
  // there are no findings to include. Rows whose plan item carries no
  // `auditKind` count as itemised — that is the pre-round-9 reading, and it
  // keeps this line's wording unchanged wherever the kind is unknown rather
  // than asserting an absence we cannot demonstrate.
  const auditItemisedCount = decisionModel.matrixRows
    .filter(row => row.auditRecommended && row.auditKind !== 'flagged').length;
  const [activeLane, setActiveLane] = useState<
    'findings' | 'risks' | 'gaps' | 'conditions'
  >('findings');
  // Section tabs under the hero (William 2026-08-03): Decision basis (matrix)
  // and Cross-domain record (lanes + history) render one at a time; the
  // inactive panel stays mounted under `hidden` so testids keep resolving.
  type FindingsSectionId = 'basis' | 'optional' | 'record' | 'accountability';
  const [activeSection, setActiveSection] = useState<FindingsSectionId>(
    'basis',
  );
  useEffect(() => {
    if (!focusAnalysisId || surface !== 'combined') return;
    // Call-ready Findings keeps the full analysis matrix under Analyses;
    // legacy Findings owns it under Decision basis.
    setActiveSection(callReadyEnabled ? 'optional' : 'basis');
  }, [callReadyEnabled, focusAnalysisId, surface]);
  const showDecision = surface === 'decision'
    || (surface === 'combined' && activeSection === 'basis');
  const showAnalyses = surface === 'analyses'
    || (surface === 'combined' && activeSection === 'optional');
  const showStakeholder = surface === 'stakeholder';
  const coverageRatio =
    pathToGo?.adequacyGate.coverageRatio
    ?? effectiveVerdict?.coverageRatio
    ?? null;
  // Verdict-strip derivations (redesign, William 2026-08-03). The old hero
  // said "no" five ways — verdict chip + SIGNAL stat, the adequacy sentence
  // twice — across 230px, and never named a next step. The strip states each
  // fact once and derives the single next action from the matrix itself.
  const requiredRows = decisionModel.matrixRows.filter(row => row.role === 'required');
  const requiredUsed = requiredRows.filter(row => row.completesStage === true).length;
  const requiredCurrent = requiredRows.filter(row => row.calculationStatus === 'current').length;
  const runningRow = decisionModel.matrixRows.find(
    row => row.completesStage !== true
      && (row.lifecycle === 'queued' || row.lifecycle === 'generating'),
  );
  const runningStage = runningRow
    ? refreshProgressByType?.[runningRow.type]?.stage ?? null
    : null;
  const reviewedStageRollup = pathToGo?.decisionRollup?.stages
    ? screeningCockpit
      ? pathToGo.decisionRollup.stages.screening
      : pathToGo.decisionRollup.stages.validation
    : null;
  const checkpoints = (screeningCockpit ? [{
    id: 'analysis',
    label: 'Screening outcomes',
    detail: screeningStage ? stageLine(screeningStage) : `${requiredUsed} of ${requiredRows.length} required screening results used`,
    complete: screeningStage?.complete === true,
    pending: screeningStage?.complete !== true && decisionModel.latestAnalyses.length > 0,
  }] : readinessCheckpoints(model, effectiveVerdict, pathToGo))
    .map(checkpoint => (
      checkpoint.id === 'analysis' && runningRow
        ? {
            ...checkpoint,
            running: true,
            detail: `${checkpoint.detail} · ${runningRow.label} running${runningStage ? ` — ${runningStage}` : ''}`,
          }
        : checkpoint
    ));
  if (surface !== 'combined' && analysesState === 'loading' && (showDecision || showAnalyses)) {
    const loadingLabel = showDecision ? 'decision basis' : 'analyses';
    return (
      <article
        className={cn('space-y-5 p-1 font-sans', className)}
        aria-busy="true"
        aria-labelledby="findings-dossier-loading-title"
        data-testid="findings-dossier"
        data-render-mode={surface}
      >
        <h1 id="findings-dossier-loading-title" className="sr-only">
          {site.name} — loading {loadingLabel}
        </h1>
        <div className="flex min-h-40 items-center justify-center rounded-lg border border-border-subtle bg-card/40 px-5 py-8">
          <div className="flex items-center gap-3 text-sm text-text-secondary">
            <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
            <span>Loading current {loadingLabel}…</span>
          </div>
        </div>
      </article>
    );
  }
  if (showStakeholder && analysesState === 'loading' && callReadyModel) {
    return (
      <article
        className={cn('space-y-5 font-sans', className)}
        aria-busy="true"
        aria-labelledby="findings-dossier-loading-title"
        data-testid="findings-dossier"
        data-render-mode="stakeholder"
      >
        <h1 id="findings-dossier-loading-title" className="sr-only">{site.name} — loading stakeholder brief</h1>
        {briefScreening && <BriefScreeningDecision site={site} review={scopedBriefScreening} onReview={onReviewScreeningDecision} />}
        <CallReadyFindingsSummary
          model={callReadyModel}
          economicResearch={economicResearch}
          economicWorkforceProfile={economicWorkforceProfile}
          onOpenPhase={onOpenPhase}
          onOpenRoute={onOpenBriefRoute}
          phaseDestinationLabels={phaseDestinationLabels}
          pathToGoAnalysisOpenCount={pathToGo?.phases.find(phase => phase.id === 'build')?.openCount}
        />
      </article>
    );
  }
  return (
    <article
      className={cn('space-y-5 font-sans', className)}
      aria-labelledby="findings-dossier-title"
      data-testid="findings-dossier"
      data-render-mode={surface === 'combined'
        ? (callReadyEnabled ? 'decision-cockpit' : 'continuous')
        : surface}
    >
      <h1 id="findings-dossier-title" className="sr-only">
        {site.name} — {
          surface === 'analyses'
            ? 'analyses'
            : surface === 'stakeholder'
              ? 'stakeholder brief'
              : 'decision dossier'
        }
      </h1>
      {surface === 'combined' && callReadyEnabled ? (
        <div className="flex items-center border-b border-border-subtle" role="tablist" aria-label="Dossier sections">
          {([['basis', 'Decision'], ['optional', 'Analyses']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeSection === id}
              onClick={() => setActiveSection(id)}
              data-testid={`findings-section-tab-${id}`}
              className={cn(
                '-mb-px inline-flex shrink-0 items-center border-b-[1.5px] px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors motion-reduce:transition-none',
                activeSection === id
                  ? 'border-text-primary text-text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {/*
        Verdict strip (2026-08-03). Deliberate cuts from the old 230px hero:
          - site name + address — already on screen (sidebar card, tab title);
            the dossier lives inside the site's page and need not introduce it.
            `findings-dossier-title` moves to the visually-hidden h1 so the
            aria-labelledby contract survives.
          - SIGNAL stat — duplicated the verdict; the verdict now lives once,
            in the lede sentence (which also absorbs the adequacy reason the
            old hero printed twice).
        Additions: gate running-state with the live stage, and the Next line —
        the one thing the old hero never said.
      */}
      {/* Editorial masthead (2026-08-17) — the verdict strip sheds its card
          box and left accent bar (accent rules were removed report-wide,
          William QA 2026-06-28) and composes as a journal masthead: overline,
          the verdict as the DISPLAY statement, microstats as a baseline
          colophon, then the page's opening 1.5px ink rule with the readiness
          phases as front-matter apparatus beneath it. */}
      {showDecision ? (
      callReadyEnabled ? <header className="space-y-4 border-b border-border-subtle pb-5">
        <div role="group" aria-label="Decision stage review" className="flex flex-wrap gap-2" data-testid="findings-decision-stage-review">
          {([1, 2] as const).map(stage => (
            <button key={stage} type="button" aria-pressed={selectedDecisionStage === stage}
              onClick={() => selectDecisionStage(stage)}
              className={cn('min-h-11 rounded-md border px-4 type-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', selectedDecisionStage === stage ? 'border-primary bg-primary/5 text-primary' : 'border-border text-text-secondary hover:bg-surface-hover')}>
              Stage {stage} · {stage === 1 ? 'Screening' : 'Final decision'}
            </button>
          ))}
        </div>
        <div className="max-w-3xl">
          <p className="text-xl leading-snug md:text-2xl" data-testid="findings-verdict-lede">
            <span className={cn('font-semibold tracking-[-0.02em]', !screeningCockpit && !awaitingStageReports && effectiveVerdict?.dataAdequate ? bandToneClass(effectiveVerdict.bandColor) : 'text-text-primary')}>
              {readiness.label}
            </span>
            <span className="mt-2 block type-body text-text-secondary"
              {...(awaitingStageReports ? { role: 'status', 'data-testid': 'findings-awaiting-stage-reports' } : {})}>
              {screeningCockpit && awaitingStageReports
                ? 'Awaiting an eligible saved screening report. Pending and held results do not supply screening signals.'
                : readiness.detail}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="findings-selected-stage-source">
          <p className="max-w-prose type-body text-text-secondary">
            {awaitingStageReports
              ? `Next: review missing or held ${screeningCockpit ? 'screening' : 'validation'} results in Analyses.`
              : `${decisionModel.latestAnalyses.length} eligible ${screeningCockpit ? 'saved screening' : 'generated validation'} report${decisionModel.latestAnalyses.length === 1 ? '' : 's'} supply this review.`}
          </p>
          {onOpenAnalyses && <Button type="button" variant={awaitingStageReports ? 'mission-primary' : 'outline'} className="min-h-11"
            onClick={() => onOpenAnalyses(decisionModel.latestAnalyses[0]?.id ?? null)}>
            Open {screeningCockpit ? 'screening' : 'validation'} analyses
          </Button>}
        </div>
        <details className="text-text-secondary" data-testid="findings-source-details">
          <summary className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md type-body text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />Source and requirement details
          </summary>
          <div className="space-y-3 border-t border-border-subtle pt-3 text-sm">
            <p className="text-text-muted">Review view: Stage {selectedDecisionStage} · Workflow stage: {briefDecisionStage?.currentStage ?? 'not available'}</p>
            <p>{decisionModel.latestAnalyses.length} eligible {screeningCockpit ? 'saved screening' : 'generated validation'} report{decisionModel.latestAnalyses.length === 1 ? '' : 's'} are available for the signals shown here.</p>
            {reviewedStageRollup ? <div data-testid={`findings-microstat-stage-${selectedDecisionStage}`}>
              <p>Requirement checks: <strong className="tabular-nums">{reviewedStageRollup.qualifiedTypes.length}/{reviewedStageRollup.requiredTypes.length}</strong> qualified for Stage {selectedDecisionStage}.</p>
              <p className="mt-1 max-w-prose text-text-muted">These checks can include semantic or workspace evidence. They are separate from the eligible report bodies displayed above; final GO still requires the current decision gate and human review.</p>
              {reviewedStageRollup.locked && <p className="mt-1 text-text-muted">Promote a generation scenario to unlock validation.</p>}
            </div> : <p>Required results used: {requiredRows.length > 0 ? `${requiredUsed}/${requiredRows.length}` : 'no requirement count available'}.</p>}
            {screeningRowReport ? <p className="text-text-muted" data-testid="findings-screening-sed-currency">
              Screening Assessment · {screeningSedCurrencyLine(screeningRowReport, currentSedVersion, pathToGo?.reportPlan)}
            </p> : null}
            {!screeningCockpit && !awaitingStageReports ? <div className="space-y-1 text-text-muted" data-testid="findings-validation-sed-currency">
              {decisionModel.latestAnalyses.map(report => <p key={report.id}>{analysisLabel(analysisType(report))} · {screeningSedCurrencyLine(report, currentSedVersion, pathToGo?.reportPlan)}</p>)}
            </div> : null}
            {auditRecommendedCount > 0 && <p className="max-w-prose text-text-muted" data-testid="findings-audit-pending">
              Audit recommended on {auditRecommendedCount} analys{auditRecommendedCount === 1 ? 'is' : 'es'} — {screeningCockpit
                ? 'source qualifications remain open for review; screening does not establish a final GO.'
                : auditItemisedCount === auditRecommendedCount
                  ? 'the verdict includes their findings at reduced confidence.'
                  : auditItemisedCount === 0
                    ? `${flaggedFloorClause()}; the verdict includes ${auditRecommendedCount === 1 ? 'it' : 'them'} at reduced confidence.`
                    : `${auditItemisedCount} carr${auditItemisedCount === 1 ? 'ies' : 'y'} findings to review; the verdict includes them all at reduced confidence.`}
            </p>}
          </div>
        </details>
        {decisionModel.decisionSummary ? <div className="max-w-[72ch]" data-testid="findings-decision-summary">
          <h2 className="type-body font-semibold text-text-primary">{screeningCockpit ? 'Screening synthesis' : 'Decision synthesis'}</h2>
          <p className="mt-1 text-sm leading-relaxed text-text-secondary">{decisionModel.decisionSummary}</p>
        </div> : null}
      </header> : <header className="relative">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <span className={cn('text-[11px] font-semibold uppercase tracking-[0.16em] text-primary', callReadyEnabled && 'font-mono')}>
              {callReadyEnabled ? 'Decision cockpit' : 'Live decision dossier'}{briefDecisionStage || callReadyEnabled ? ` · Stage ${selectedDecisionStage}` : ''}
            </span>
            <p
              className={cn('mt-2 max-w-3xl text-xl leading-snug md:text-2xl', callReadyEnabled && 'font-sans tracking-[-0.02em]')}
              data-testid="findings-verdict-lede"
            >
              <span
                className={cn(
                  'font-bold',
                  !screeningCockpit && effectiveVerdict?.dataAdequate
                    ? bandToneClass(effectiveVerdict?.bandColor)
                    : 'text-warning',
                )}
              >
                {readiness.label}
              </span>
              <span className="text-text-secondary"> — {readiness.detail}</span>
            </p>
            {screeningRowReport ? (
              <p className="mt-1 text-[11px] text-text-muted" data-testid="findings-screening-sed-currency">
                Screening Assessment · {screeningSedCurrencyLine(screeningRowReport, currentSedVersion, pathToGo?.reportPlan)}
              </p>
            ) : null}
            {callReadyEnabled && !screeningCockpit && !awaitingStageReports ? (
              <div className="mt-1 space-y-1 text-[11px] text-text-muted" data-testid="findings-validation-sed-currency">
                {decisionModel.latestAnalyses.map(report => (
                  <p key={report.id}>
                    {analysisLabel(analysisType(report))} · {screeningSedCurrencyLine(report, currentSedVersion, pathToGo?.reportPlan)}
                  </p>
                ))}
              </div>
            ) : null}
            {/* AUDIT-ADVISORY (2026-08-22) — holds never gate the verdict;
                this one muted line is the whole warning. Count comes off the
                plan items' auditRecommended flag. */}
            {auditRecommendedCount > 0 && (
              <p
                className="mt-1 text-[11px] text-text-muted"
                data-testid="findings-audit-pending"
              >
                Audit recommended on {auditRecommendedCount} analys{auditRecommendedCount === 1 ? 'is' : 'es'} — {
                  screeningCockpit
                    ? 'source qualifications remain open for review; screening does not establish a final GO.'
                    : auditItemisedCount === auditRecommendedCount
                    ? 'the verdict includes their findings at reduced confidence.'
                    : auditItemisedCount === 0
                      ? `${flaggedFloorClause()}; the verdict includes ${auditRecommendedCount === 1 ? 'it' : 'them'} at reduced confidence.`
                      : `${auditItemisedCount} carr${auditItemisedCount === 1 ? 'ies' : 'y'} findings to review; the verdict includes them all at reduced confidence.`
                }
              </p>
            )}
          </div>
          {callReadyEnabled && (
            <div role="group" aria-label="Decision stage review" className="flex shrink-0 flex-wrap gap-2" data-testid="findings-decision-stage-review">
              {([1, 2] as const).map(stage => (
                <button key={stage} type="button" aria-pressed={selectedDecisionStage === stage}
                  onClick={() => selectDecisionStage(stage)}
                  className={cn('min-h-11 rounded-md border px-3 type-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', selectedDecisionStage === stage ? 'border-primary bg-primary/5 text-primary' : 'border-border text-text-secondary hover:bg-surface-hover')}>
                  Stage {stage} · {stage === 1 ? 'Screening' : 'Final decision'}
                </button>
              ))}
            </div>
          )}
          <dl
            className="flex shrink-0 items-baseline gap-x-2.5 gap-y-1"
            data-testid="findings-microstats"
          >
            {!screeningCockpit && !callReadyEnabled ? <div className="flex items-baseline gap-1.5">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Coverage</dt>
              <dd className="text-sm font-bold tabular-nums text-text-primary">
                {coverageRatio == null ? '—' : `${Math.round(coverageRatio * 100)}%`}
              </dd>
            </div> : null}
            {!screeningCockpit && !callReadyEnabled ? <span className="text-text-muted" aria-hidden>·</span> : null}
            <div className="flex items-baseline gap-1.5">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Sources</dt>
              <dd className="text-sm font-bold tabular-nums text-text-primary">
                {decisionModel.latestAnalyses.length}
              </dd>
            </div>
            {pathToGo?.decisionRollup?.stages ? (
              // TWO-STAGE: one count per stage, in pipeline order. A locked
              // Stage 2 is muted (it cannot move yet), never red.
              ([pathToGo.decisionRollup.stages.screening, pathToGo.decisionRollup.stages.validation] as const)
                .filter(stage => !briefDecisionStage && !callReadyEnabled || stage.stage === selectedDecisionStage).map((stage) => (
                <React.Fragment key={stage.stage}>
                  <span className="text-text-muted" aria-hidden>·</span>
                  <div className="flex items-baseline gap-1.5" data-testid={`findings-microstat-stage-${stage.stage}`}>
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">{callReadyEnabled ? 'Current required' : `Stage ${stage.stage}`}</dt>
                    <dd
                      className={cn(
                        'text-sm font-bold tabular-nums',
                        stage.requiredTypes.length === 0 || stage.complete
                          ? 'text-success'
                          : stage.locked ? 'text-text-muted' : 'text-danger',
                      )}
                      title={stage.locked ? 'Promote a generation scenario to unlock validation' : undefined}
                    >
                      {stage.requiredTypes.length > 0 ? `${stage.qualifiedTypes.length}/${stage.requiredTypes.length}` : '—'}
                    </dd>
                  </div>
                </React.Fragment>
              ))
            ) : (
              <>
                <span className="text-text-muted" aria-hidden>·</span>
                <div className="flex items-baseline gap-1.5">
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Used</dt>
                  <dd
                    className={cn(
                      'text-sm font-bold tabular-nums',
                      requiredRows.length > 0 && requiredUsed === requiredRows.length
                        ? 'text-success'
                        : 'text-danger',
                    )}
                  >
                    {requiredRows.length > 0 ? `${requiredUsed}/${requiredRows.length}` : '—'}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </div>

        <div
          aria-hidden
          className="mt-4 h-[1.5px] w-full origin-left animate-[findings-rule-draw_0.5s_ease-out] bg-text-primary motion-reduce:animate-none"
        />

        {showDecision ? (
          <>
            {callReadyEnabled ? (
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border-subtle py-3 type-body text-text-secondary" data-testid="findings-selected-stage-source">
                <p>{decisionModel.latestAnalyses.length} eligible {screeningCockpit ? 'saved screening' : 'generated validation'} report{decisionModel.latestAnalyses.length === 1 ? '' : 's'} · {screeningCockpit ? 'Screening options to review; source qualifications are preserved.' : 'Validation signals use these generated reports only.'}</p>
                <p className="type-meta text-text-muted">Review view: Stage {selectedDecisionStage} · Workflow stage: {briefDecisionStage?.currentStage ?? 'not available'}</p>
                {onOpenAnalyses && <button type="button" onClick={() => onOpenAnalyses(decisionModel.latestAnalyses[0]?.id ?? null)} className="min-h-11 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Open {screeningCockpit ? 'screening' : 'validation'} analyses</button>}
              </div>
            ) : <ReadinessSpine checkpoints={checkpoints} />}

            {decisionModel.decisionSummary ? (
              // Editorial pull-quote: the synthesis keeps its primary wash as a
              // highlight-marker block — square, unbordered, no icon — with a
              // small-caps label and the prose held to the reading measure.
              <div
                className="mt-3 bg-primary/5 px-4 py-3"
                data-testid="findings-decision-summary"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">{screeningCockpit ? 'Screening synthesis' : 'Decision synthesis'}</p>
                <p className="mt-1 max-w-[72ch] text-sm leading-relaxed text-text-primary">
                  {decisionModel.decisionSummary}
                </p>
              </div>
            ) : null}
          </>
        ) : null}
      </header>
      ) : null}

      {/* Top-level contents line (2026-08-17) — the dossier's section tabs
          share the lane spine's grammar: small-caps entries on a hairline,
          active entry underlined with the system's 1.5px ink rule. */}
      {surface === 'combined' && !callReadyEnabled ? (
      <div className="flex flex-wrap items-center border-b border-border-subtle" role="tablist" aria-label="Dossier sections">
        {([['basis', 'Decision basis'], ['optional', 'Analysis library'], ['record', 'Findings summary'], ['accountability', 'Accountability']] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeSection === id}
            onClick={() => setActiveSection(id)}
            data-testid={`findings-section-tab-${id}`}
            className={cn(
              '-mb-px inline-flex items-center border-b-[1.5px] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors',
              activeSection === id
                ? 'border-text-primary text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      ) : null}

      {callReadyEnabled && callReadyModel && showStakeholder ? (
        <div>
          {briefScreening && <BriefScreeningDecision site={site} review={scopedBriefScreening} onReview={onReviewScreeningDecision} />}
          <CallReadyFindingsSummary
            model={callReadyModel}
            onOpenAnalysis={onOpenAnalysis}
            onOpenPhase={onOpenPhase}
            onOpenRoute={onOpenBriefRoute}
            phaseDestinationLabels={phaseDestinationLabels}
            pathToGoAnalysisOpenCount={pathToGo?.phases.find(phase => phase.id === 'build')?.openCount}
            onRunEconomicScreen={onRunEconomicScreen}
            economicResearch={economicResearch}
            economicWorkforceProfile={economicWorkforceProfile}
          />
        </div>
      ) : null}

      {surface === 'combined' || showDecision ? (
      <div className={showDecision ? undefined : 'hidden'}>
        {screeningCockpit ? (
          <>
            <SiteRecommendedPath
              siteId={site.id}
              screeningReports={stageModel.latestAnalyses}
              onReview={() => {
                if (onOpenBriefRoute) onOpenBriefRoute({ kind: 'stage2-decision', id: 'power-supply' },
                  { questionId: 'resources', questionLabel: 'Power & cooling' });
                else onOpenPhase?.('build');
              }}
            />
            {awaitingStageReports && !callReadyEnabled ? <p role="status" className="py-5 type-body text-text-secondary" data-testid="findings-awaiting-stage-reports">Awaiting an eligible saved screening report. A pending or held report does not supply screening signals.</p> : !awaitingStageReports && <DecisionSignalSummary model={stageModel} />}
          </>
        ) : callReadyEnabled ? (
          !awaitingStageReports && <DecisionSignalSummary model={stageModel} />
        ) : (
          <AnalysisMatrix
              journey={journey}
              composite={composite}
              onRunNextAction={onRunNextAction}
            model={model}
            planItems={pathToGo?.reportPlan ?? null}
            stages={pathToGo?.decisionRollup?.stages ?? null}
            decision={stripDecisionFor(effectiveVerdict)}
            focusAnalysisId={focusAnalysisId}
            onFocusAnalysisHandled={onFocusAnalysisHandled}
            onOpenAnalysis={onOpenAnalysis}
            onResolveInputs={onResolveInputs}
            onPlanAction={onPlanAction}
            refreshErrorByType={refreshErrorByType}
            autoRebindByType={autoRebindByType}
            refreshProgressByType={refreshProgressByType}
            onCancelRun={onCancelRun}
            currentSedVersion={currentSedVersion}
            siteId={site.id}
          />
        )}
      </div>
      ) : null}

      {surface === 'combined' || showAnalyses ? (
      <div className={showAnalyses ? undefined : 'hidden'}>
        {callReadyEnabled || surface === 'analyses' ? (
          <div className="space-y-5">
            <AnalysisMatrix
              journey={journey}
              composite={composite}
              onRunNextAction={onRunNextAction}
              showDecisionPath={surface !== 'analyses'}
              model={model}
              planItems={pathToGo?.reportPlan ?? null}
              stages={pathToGo?.decisionRollup?.stages ?? null}
              decision={stripDecisionFor(effectiveVerdict)}
              focusAnalysisId={focusAnalysisId}
              onFocusAnalysisHandled={onFocusAnalysisHandled}
              onOpenAnalysis={onOpenAnalysis}
              onResolveInputs={onResolveInputs}
              onPlanAction={onPlanAction}
              refreshErrorByType={refreshErrorByType}
              autoRebindByType={autoRebindByType}
              refreshProgressByType={refreshProgressByType}
              onCancelRun={onCancelRun}
              currentSedVersion={currentSedVersion}
              siteId={site.id}
            />
            <OptionalAnalyses
              analyses={analyses}
              matrixRows={model.matrixRows}
              planItems={pathToGo?.reportPlan}
              isDatacrateAdmin={isDatacrateAdmin === true}
              onOpenAnalysis={onOpenAnalysis}
              onLaunchAnalysis={onLaunchAnalysis}
              onPlanAction={onPlanAction}
              inflightByType={inflightByType}
              siteId={site.id}
              refreshProgressByType={refreshProgressByType}
              onCancelRun={onCancelRun}
              refreshErrorByType={refreshErrorByType}
              autoRebindByType={autoRebindByType}
            />
          </div>
        ) : (
        <OptionalAnalyses
          analyses={analyses}
          matrixRows={model.matrixRows}
          planItems={pathToGo?.reportPlan}
          isDatacrateAdmin={isDatacrateAdmin === true}
          onOpenAnalysis={onOpenAnalysis}
          onLaunchAnalysis={onLaunchAnalysis}
          onPlanAction={onPlanAction}
          inflightByType={inflightByType}
          siteId={site.id}
          refreshProgressByType={refreshProgressByType}
          onCancelRun={onCancelRun}
          refreshErrorByType={refreshErrorByType}
          autoRebindByType={autoRebindByType}
        />
        )}
      </div>
      ) : null}

      {surface === 'combined' && !callReadyEnabled ? (
      <div className={activeSection === 'record' ? undefined : 'hidden'}>
      <div>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="type-meta uppercase tracking-[0.14em] text-primary">
              Findings summary
            </p>
            <h2 className="mt-1 type-section text-text-primary">
              Findings and actions
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {(() => {
              // The Executive Summary report IS this tab's long-form twin —
              // surface it here (William, 2026-08-11). `onOpenAnalysis`
              // routes real report ids to the report reader.
              const summaryReport = model.latestAnalyses.find(
                (report) => analysisType(report) === 'executive-summary',
              );
              return summaryReport && onOpenAnalysis ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onOpenAnalysis(summaryReport.id)}
                  data-testid="findings-open-summary-report"
                >
                  Open summary report
                  <ArrowUpRight aria-hidden />
                </Button>
              ) : null;
            })()}
            <p className="hidden max-w-lg text-right text-xs italic text-text-muted md:block">
              Repeated conclusions are merged while every contributing analysis
              remains visible.
            </p>
          </div>
        </div>
        {/* Findings-B lane split (2026-08-03): the five lanes cost ~700px of
            stacked cards even when near-empty. A spine carries every lane's
            count at rest; one lane renders at a time. Inactive lanes stay
            MOUNTED under `hidden` so their content and testids remain
            resolvable (StudioPopover precedent). */}
        {/* Editorial CONTENTS LINE (2026-08-17) — the lane spine is a table
            of contents, not a row of pill buttons: small-caps entries on a
            shared hairline, the active entry marked by the same 1.5px ink
            underline the lane tables use. One rule system, top to bottom. */}
        <div className="flex flex-wrap items-center border-b border-border-subtle" role="tablist" aria-label="Cross-domain lanes">
          {([
            ['findings', 'Key findings', model.findings.length],
            ['risks', 'Risks', model.risks.length],
            ['gaps', 'Gaps', model.gaps.length],
            ['conditions', 'Conditions', model.conditions.length],
          ] as const).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeLane === id}
              onClick={() => setActiveLane(id)}
              data-testid={`findings-lane-tab-${id}`}
              className={cn(
                '-mb-px inline-flex items-baseline gap-1.5 border-b-[1.5px] px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors',
                activeLane === id
                  ? 'border-text-primary text-text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              {label}
              <span className="tabular-nums opacity-75">{count}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 grid items-start gap-4">
          <div className={activeLane === 'findings' ? undefined : 'hidden'}>
          <EvidenceLane
            id="findings"
            ordinal={1}
            title="Key findings"
            description="Strengths, opportunities, and cross-domain conclusions."
            items={model.findings}
            contradictions={model.screeningContradictions}
            tone="positive"
          />
          </div>
          <div className={activeLane === 'risks' ? undefined : 'hidden'}>
          <EvidenceLane
            id="risks"
            ordinal={2}
            title="Risks and constraints"
            description="Material issues that can weaken or stop the project."
            items={model.risks}
            contradictions={model.screeningContradictions}
            tone="risk"
          />
          </div>
          <div className={activeLane === 'gaps' ? undefined : 'hidden'}>
          <EvidenceLane
            id="gaps"
            ordinal={3}
            title="Evidence gaps"
            description="Missing, stale, unavailable, or quality-held decision inputs."
            items={model.gaps}
            contradictions={model.screeningContradictions}
            tone="gap"
          />
          </div>
          <div className={activeLane === 'conditions' ? undefined : 'hidden'}>
          <EvidenceLane
            id="conditions"
            ordinal={4}
            title="Decision conditions"
            description="Conditions that must remain attached to a qualified decision."
            items={model.conditions}
            contradictions={model.screeningContradictions}
            tone="condition"
          />
          </div>
        </div>
      </div>

      </div>
      ) : null}

      {surface === 'combined' && !callReadyEnabled ? (
      <div className={activeSection === 'accountability' ? undefined : 'hidden'}>
      <HistoryPanel model={model} decisionRecords={decisionRecords} />
      </div>
      ) : null}
    </article>
  );
});
